// ============================================================
// TERROR — THE UNSTABLE WORLD  (the overworld screen)
// ------------------------------------------------------------
// M11 — "THE PAINTED WORLD." caP'n shipped `overworld.webp`, a
// finished hand-painted SMW overworld, and it replaced the
// procedural one outright.
//
// ── WHAT CHANGED, STRUCTURALLY ──────────────────────────────
// M10 generated the world: `worldRender.js` authored five
// procedural islands, contoured a seeded heightfield six times,
// stacked the contours as real prisms in a CSS-3D rig, and
// PROJECTED each node's region-local coordinates onto them.
// That file is DELETED. All 1797 lines of it. The painting does
// every job it did, and does them better, because a person drew
// it.
//
// What replaces it is deliberately, aggressively small: ONE
// <img>, at its own native size, inside a pan/zoom camera, with
// pins positioned absolutely on top of it in the painting's own
// pixel space. There is no terrain, no heightfield, no contour,
// no terrace, no translateZ, no `lv`, no sun vector, no drift, no
// weather. `worldData.js` is the only import that carries state.
//
// ── THE FLAT LAW (why the tilt rig had to go, not just idle) ──
// The obvious move was to keep the 3D rig and hang the painting
// on it as a ground texture. That is wrong, and it is worth
// writing down so nobody re-tries it: the art is ALREADY drawn in
// isometric perspective with a baked light source and baked cast
// shadows. Tilting it adds a second, disagreeing perspective on
// top of the first — parallel edges converge twice, the painted
// shadows point one way while the rig's implied sun points
// another, and the eye reads the whole thing as a photograph of a
// map lying on a table rather than as a world.
//
// A painted overworld is FLAT ON PURPOSE. Super Mario World is
// flat. The tilt was never the effect; the tilt was scaffolding
// holding up a generated landscape that had nothing else to make
// it read as ground. The painting doesn't need the scaffolding.
//
// ── THE ROUTE OVERLAY, AND WHY IT CAME BACK ─────────────────
// It was deleted this session on caP'n's call — "the picture is
// enough of a clear path, no redundancy" — and then restored an
// hour later on a better one: *"I need to make sure everything
// lines up."*
//
// That reversal is the correct engineering answer and worth
// recording, because the two positions are not in conflict. A
// route overlay is redundant DECORATION and it is indispensable
// INSTRUMENTATION. Pins are authored against a painting by hand;
// the only way to see whether a pin sits on its painted pad, and
// whether the graph agrees with the roads somebody drew, is to
// draw the graph on top of the painting and look. Hiding the
// reference lines while trying to align to them is working blind.
//
// So the overlay lives, and the shipping question — how much of it
// a PLAYER should see — is now a single named dial, below.
//
// ── THE TWO-COLOUR LAW (caP'n) ──────────────────────────────
// "make the 1 main path that branches out towards the rest of the
// map red, every other path — the path that shows where the secret
// areas take you, but not the actual secret area itself — should be
// yellow. This means the eye's path should be red around the nodes,
// but the broken path that is telling you how to get there should
// be yellow."
//
//   RED    a road you WALK. Solid, routed along the painted road
//          underneath it. This is the whole traversable world, one
//          connected spine with branches — including the roads
//          INSIDE a secret area, because once you are standing in
//          the Silver Lining its spiral ring is just a road.
//   YELLOW a link you TAKE. Broken, never routed, always straight.
//          Warps and secret exits: the line exists to say "this
//          connects to that," and a dash says that better than a
//          curve implying a walk nobody takes.
//
// The distinction is not secret-vs-known, it is WALK vs TAKE. That
// is why the Eye's own ring is red while every route into the Eye
// is yellow — and it is why the colour is read off `via` rather
// than off which region an edge lands in.
//
// ── WHAT SURVIVED, DELIBERATELY ─────────────────────────────
// Every line of game wiring, untouched: same buildCtx, pathState,
// reachableSet, neighborsOf, nodeState, secretOffered, same
// append-only Firebase stamps, same one-step-at-a-time travel
// rule, same onFight handoff. The run state on screen is REAL
// SAVE DATA — there is no demo dial anywhere in this file.
//
// ── THE CAST IS GONE (caP'n, this session) ──────────────────
// "remove the shadow overlay people."
//
// The paper cast was a warden doll beside every boss node, a child
// doll at each Escling, and a horned Lord at the Seal — flat
// billboards with contact shadows, inherited from M10. On
// generated terrain they read as figures standing on ground. On
// the painting they read as what they technically are: solid black
// silhouettes pasted over somebody's artwork, twenty of them,
// each one hiding a piece of the thing they were standing on.
//
// The generated map had empty ground for a doll to occupy. A
// painting has no empty ground — every pixel is already doing a
// job. That is the whole difference, and it retires the technique
// here rather than fixing it.
//
// The PLAYER's sprite stays, with its shadow. One figure that
// represents you is not clutter; it is the reason there is a
// camera. `Sprite`, `CompanionSprite` and `.wmPaper` all remain in
// service of that one doll.
//
// ── THE ART IS ONE STRING ───────────────────────────────────
// `cdnUrl(MAP.file)` out of `CDN.js`. NO `crossOrigin` attribute
// anywhere near it — terror-assets sends no CORS headers, and
// setting the attribute is what turns card art into dark slabs.
// The filename is lowercase because the CDN is case-sensitive,
// and it lives in `worldData.MAP` rather than being typed here,
// so the data file stays the one place the world is described.
//
// ── THE FRAME LAW (July 29) ─────────────────────────────────
// caP'n, with two screenshots: zoomed out, "it's just clearly an
// image now, not even a map I am exploring"; zoomed in, "the menu
// and all my other options are gone."
//
// Two complaints, two entirely unrelated causes, and it is worth
// keeping them apart because the second one was not in this file's
// logic at all.
//
//   THE POSTCARD was the camera: a fixed `ZMIN = 0.18` floor and
//   no pan clamp of any kind. Both are now gone — see THE FRAME
//   LAW below. The floor is computed from the live viewport as
//   the widest zoom that still COVERS the frame, the pan is
//   clamped to the painting's own edges, and the "World" pill
//   that offered the postcard shot on purpose is deleted.
//
//   THE VANISHED CHROME was never this component. Nothing here
//   hides the title or the pills — they are absolutely-positioned
//   siblings and the camera transform cannot reach them. The
//   BROWSER was zoomed, by a trackpad pinch that this file was
//   trying and failing to swallow, because React registers
//   `wheel` as a passive listener and the `preventDefault` in the
//   old JSX handler had never once executed. See THE WHEEL,
//   ATTACHED BY HAND.
//
// The lesson worth keeping is the second one: when UI that cannot
// be unmounted goes missing, suspect the viewport before the
// component.
//
// ── PERF ────────────────────────────────────────────────────
// One image, one SVG of routes and 46 pins. That is the entire
// scene graph — down from ~140 composited layers and 2.9 MB of
// generated markup. Panning is one transform on one element.
// Walking is one rAF writing `left`/`top` on two refs; no
// setState in the loop, ever. `ie-realtime-perf` holds trivially
// now, which is its own argument for the swap.
// ============================================================

import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import CompanionSprite from "./CompanionSprite";
import { useEquippedCompanion } from "./useEquippedCompanion";
import { Sprite } from "./Sprite";
import { getPlayerSprite, getRestSprite } from "./SpriteManifest";
import { cdnUrl } from "./CDN";
import RoomBackdrop from "./RoomBackdrop";
// ⬡ V'AEL'S TOOLS and THE POP WINDOW — see vaelsTools.js / WorldNotice.js
import { paceFor, toolBrokenBy, stepCost, addSteps, ledgerLine, toolsHeld } from "./vaelsTools";
import WorldNotice, { noticesFor, noticeForBreak } from "./WorldNotice";
import {
  MAP, NODE_BY_ID, PATHS, CHILDREN, START_NODE, SURVEYOR,
  buildCtx, pathState, reachableSet, neighborsOf, nodeState,
  discoveredRegions, secretOffered, childrenFound,
  nodeRole, roleLabel, chamberHint, shardsFor, SHARD_TOTAL,
  openEmperium, MARK_ROLES,
  // ⭐ 2026-08-21 — THE SKY DOORS and the Eye's two keys
  skyDoorStates, eyeOpen, saveSkyFrom, recordBeat, saveVeilLedger,
  SECRET_NODES, secretsFoundCount,
  // ⭐ 2026-08-20 — THE MOON, THE AMBUSH, THE EGG-DOOR
  EYE_NODE, eyeStepFrom, pendingAmbush, opponentForAmbush,
  opponentForNode, resolveBoss, regionHue, edgeKey, routePoints,
  // M10 — the spoken pin label names its region
  REGION_BY_ID,
  loadWorldProgress, saveWorldPosition,
  // ⬡ THE WORLD IS PLURAL. `MAP` above is still Minerium 1's painting
  // and every module-level default below still falls back to it, so
  // nothing here changed behaviour on the day this landed. What
  // changed is that the camera and the render now ASK which painting
  // they are looking at instead of assuming.
  mapOf, nodesIn, pathsIn, worldOfNode,
} from "./worldData";

const GM = {
  page: "#0f0c07", pageMid: "#241c0c", pageBorder: "#3d2e14",
  inkGold: "#c8963c", inkGoldBright: "#e8b84b",
  vellum: "#e8d5a3", vellumDim: "#b89a5a",
  font: "'Cinzel', 'Palatino Linotype', serif",
};
const SILVER = "#e9edf6";
const PINK   = "#ff2266";

// THE TWO ROUTE COLOURS. Deliberately not the region hues — a route
// is now telling you what KIND of connection it is, and region
// colour was competing with that answer.
// ═══════════════════════════════════════════════════════════════
// ⚑ THE BARE MAP  (caP'n, August 17, 2026)
// ═══════════════════════════════════════════════════════════════
// "let's remove the icons you put over and just leave the ones that
//  are there already... see all these red lines connecting to each
//  other? dont need them... leave all the yellow dashed lines so I
//  can see if they line up properly later."
//
// THREE DIALS, and they are dials rather than deletions on purpose.
// The road graph is about to be re-authored — it currently connects
// far more pads to each other than the painting's causeways
// actually do — and you cannot judge a painted road while a red
// line is drawn over it. So the overlay steps back and lets the
// ART be the map:
//
//   SHOW_PIN_MARKS   — the glyph discs. OFF: the painted pads are
//                      already marks, and drawing a second mark on
//                      top of a mark is why the screen read as
//                      cluttered. The pins stay TAPPABLE — an
//                      invisible hit area sits on every node, so
//                      nothing about walking, selecting or warping
//                      changes. Only the paint changes.
//   SHOW_WALK_ROUTES — the red roads. OFF while the graph is
//                      re-cut against the causeways.
//   SHOW_WARP_ROUTES — the yellow dashes: the ten beacons and the
//                      secrets. ON, deliberately, because they are
//                      the thing being checked for alignment.
//
// Flip any of them back to `true` and the old screen returns
// exactly as it was. Nothing else in this file knows they exist.
const SHOW_PIN_MARKS   = false;
const SHOW_WALK_ROUTES = false;
const SHOW_WARP_ROUTES = true;

const ROUTE_WALK = "#ff3b3b";   // red    — a road you walk
const ROUTE_TAKE = "#ffc21f";   // yellow — a link you take

// MAP pixels per frame while walking. A hop between adjacent pads
// is 60-110px, so this lands a step at roughly half a second —
// long enough to read as travel, short enough not to be a wait.
// A dial; tune it in play, not here.
const STEP_SPEED = 2.4;

// ⬡ AND THE SNAIL'S PACE IS NOT HERE. Veiled, the walk runs at the
// TOOL's own `stepSpeed` (vaelsTools.js), resolved per step through
// `paceFor`. Putting a second constant beside this one would have
// meant the second tool needing a third, so the pace is a field on
// the thing that slows you down, not a dial on the screen.

// ═══════════════════════════════════════════════════════════════
// THE FRAME LAW  (caP'n, July 29: "never past, we need to be
// immersed, never let it look like an image xD")
// ═══════════════════════════════════════════════════════════════
// The old camera had a FIXED floor — `ZMIN = 0.18` — and no pan
// clamp whatsoever. Both halves of that were wrong in the same
// way: a fixed zoom number cannot know how big the window is, so
// on any desktop it eventually parks a 1054x1492 painting as a
// ~190px postcard on a flat black page, and with nothing clamping
// the pan you can then fling that postcard into a corner. At that
// moment the screen is no longer a world being explored; it is a
// JPEG being looked at, and every bit of the immersion the
// painting bought is gone.
//
// The fix is to stop expressing the floor as a number at all.
//
//   THE FLOOR IS A CONDITION, NOT A CONSTANT:
//   the widest legal shot is the one where the painting still
//   COVERS the frame on both axes. Computed from the live
//   viewport, so it is correct on a phone, on a laptop, on an
//   ultrawide, and across a window resize — none of which a
//   hardcoded 0.18 could ever be.
//
// Consequence, stated plainly because it is a real trade: you can
// no longer see the whole world at once on a wide screen. That is
// the intended behaviour — the "World" pill that used to offer
// that shot is deleted this session, on caP'n's call, because the
// shot itself was the bug in the screenshot.
//
// THE SEAM (Exception Law): if a framed overview is ever wanted
// back — a councils-of-war shot, a fast-travel picker, a "where
// am I" beat in H1 — it is this ONE NUMBER. 0.8 lets the frame
// breathe ~20% past the art on its long axis, and the equipped
// backdrop is already painted back there to catch it. Nothing
// else in the file needs to move.
const ZFLOOR_SLACK = 0.92;

// How far the painting may be scaled UP. Was 3.2, which on a
// 1054px-wide image is a 3x upscale of somebody's brushwork — the
// far end of the dial was mush, and mush reads as "an image" just
// as loudly as a postcard does. 2.0 is about as hard as this art
// can be pushed and still be art.
const ZART_CEIL = 2.0;

// ── THE WIDEST LEGAL SHOT ───────────────────────────────────────
// Cover fit, but never above ZART_CEIL, and that cap is the whole
// reason for the backdrop. A 2560x1080 ultrawide would need 2.43x
// to cover a 1054x1492 portrait painting; honouring that would
// mean a permanently blurry world on a monitor that cost more than
// the game. So on aspect ratios that extreme the cover promise is
// the thing that gives, the sides open up, and what shows through
// is the player's own equipped backdrop — which is exactly the
// case caP'n specified: "worst case scenario if anything leaks out
// it looks amazing."
// ── THE WORLD SHOT, RESTORED  (caP'n, August 17, 2026) ─────────
// "now let's just give the ability to pan and zoom out/in."
//
// THE SEAM ABOVE IS BEING PULLED, exactly as it was written to be.
// The floor was a COVER fit — the painting had to fill the frame on
// BOTH axes — and on any landscape window that is a brutal number:
// a 1900px-wide frame needs 2.26x to cover an 841px-wide painting,
// which the ZART_CEIL of 2.0 then pins it to. Floor 2.0, ceiling
// 2.0. The dial was not stiff, it was DEAD, and every screenshot
// this session was the same over-magnified corner because the
// camera had no legal way to pull back. That is the exact failure
// zCeilFor's comment predicted two constants below; it just never
// occurred to us that the FLOOR would be the side that hit the cap.
//
// The floor is now a CONTAIN fit: the widest legal shot is the one
// where the whole painting is inside the frame. It is still a
// CONDITION and not a constant — still measured off the live
// viewport, still correct on a phone and on an ultrawide.
//
// The July 29 trade is hereby re-decided, not forgotten. Yes, the
// wide shot shows you that the world is a painting. But a map you
// cannot pull back from is not immersive either — it is a keyhole,
// and coordinating ten beacons through a keyhole is not work
// anybody should have to do. ZFLOOR_SLACK below is the dial: 0.92
// leaves a thin margin of the player's equipped backdrop around
// the art at the widest shot, which is the "worst case it looks
// amazing" caP'n already signed off on. Raise it to 1.0 for edge-
// to-edge; drop it toward 0.8 to let the world breathe further.
// ⬡ `m` is the ACTIVE Minerium's painting. It defaults to Minerium 1
// so that a call site nobody updated still produces the frame it
// always produced — but note what that costs when a second painting
// exists: a MISSED call site does not throw, it silently clamps the
// camera to the wrong canvas size, which presents as "the map is
// stuck at the edge" and reads like a mystery. If you add a camera
// call, pass the map.
const zFitFor = (v, m = MAP) => Math.min(v.w / m.w, v.h / m.h);

const zFloorFor = (v, m = MAP) =>
  Math.min(zFitFor(v, m) * ZFLOOR_SLACK, ZART_CEIL);

// The ceiling is RELATIVE, and this is a bug fix rather than a
// flourish. A fixed 2.0 ceiling against a computed floor means
// that on any viewport where the floor also lands at 2.0, floor
// and ceiling are the same number and the zoom dial is silently
// DEAD — pinch, wheel and trackpad all do nothing, on a real and
// fairly common monitor, with no error anywhere. Deriving the
// ceiling from the floor guarantees the dial always has travel.
const zCeilFor = (v, m = MAP) => Math.max(ZART_CEIL, zFloorFor(v, m) * 1.6);

// ── THE ONE GATE ────────────────────────────────────────────────
// Every camera write in this file funnels through `applyCam`, and
// `applyCam` calls this. Nothing else clamps, and nothing else is
// allowed to — a legal frame is not something the callers have to
// remember to produce, it is something the renderer guarantees.
// "The handler self-gates" is not acceptable; the gate lives here.
// Mutates in place: `camState.current` IS the object passed in.
function clampCamTo(c, v, m = MAP) {
  c.z = Math.min(zCeilFor(v, m), Math.max(zFloorFor(v, m), c.z));
  const mw = m.w * c.z, mh = m.h * c.z;
  // Cover on an axis → the edge may never come inside the frame.
  // Smaller than the frame on an axis — reachable only on extreme
  // aspect ratios, or if SLACK is ever dropped below 1.0 — → centre
  // it, so the leak is symmetrical and the backdrop reads as a
  // mount around the world rather than as a gap down one side.
  c.x = mw >= v.w ? Math.min(0, Math.max(v.w - mw, c.x)) : (v.w - mw) / 2;
  c.y = mh >= v.h ? Math.min(0, Math.max(v.h - mh, c.y)) : (v.h - mh) / 2;
  return c;
}

// ── THE BACKDROP BAND ───────────────────────────────────────────
// caP'n: "throw our backdrop in the background, that way worst
// case scenario if anything leaks out it looks amazing haha."
// RoomBackdrop normally clips itself to the right chrome rail;
// `side: "full"` is its own documented no-feather, whole-surface
// mode, so the world map gets the player's equipped backdrop as
// its entire underlayer. Insurance, mostly — with SLACK at 1.0 the
// only times it shows are the load veil, a CDN miss on the
// painting, and the instant of a window resize. Those are exactly
// the three moments the screen used to look broken.
const FULL_BAND = { left: 0, top: 0, width: "100%", height: "100%", side: "full", portrait: false };

// ── SHOW_ALL_ROUTES — the alignment dial ─────────────────────
// TRUE  : every route draws, including secret exits, warps into
//         the Silver Lining, and requirement-gated doors that are
//         still shut. This is ALIGNMENT MODE — caP'n needs to see
//         the whole graph over the painting to check that pins sit
//         in their pads and that the routes agree with the roads
//         that were drawn by hand.
// FALSE : shipping behaviour. A secret is invisible until found, a
//         warp until earned, and the Silver Lining stops being
//         advertised from the first frame of a new save.
//
// One constant, not a subsystem, and it is deliberately loud: the
// day this map goes in front of a player who is meant to discover
// things, this flips to false and nothing else moves.
// TWO DIALS THAT MUST NEVER DISAGREE, so they are one. Drawing a
// route the rules will not let you walk, or hiding one they will,
// are both worse than either mode on its own — you end up debugging
// the overlay instead of the graph. `SURVEYOR` in worldData.js is
// now the single switch; this line follows it.
const SHOW_ALL_ROUTES = SURVEYOR;

// ── RESUME_IN_PLACE — the un-strand dial ─────────────────────
// FALSE : standard. A save parked on a stop you can no longer
//         reach comes home to The Roadhead on load.
// TRUE  : you resume exactly where you stood, reachable or not.
//
// ── WHY THIS EXISTS AT ALL ───────────────────────────────────
// `at` used to be validated for EXISTENCE and nothing else —
// `NODE_BY_ID[p.at] ? p.at : START_NODE`. A node that exists but
// is locked passed that check, and locked stops have no open
// roads, so the doll stood there with four dead arrow keys and
// no error anywhere. Two ordinary things put a save in that
// state: an afternoon of Surveyor walking followed by the dial
// going off, and any future authoring change that re-gates a
// road behind somebody who already walked past it.
//
// ── AND WHY IT IS A DIAL AND NOT A FIX ───────────────────────
// caP'n: "when we do hardcore mode it will need to be the other
// way, right from where I left off." Coming home is a POLICY of
// the standard mode, not a law of the world — Hardcore's whole
// proposition is that the run persists, stranded or not, and
// wanting your position back is the point of it rather than a
// bug in it. So the behaviour gets a name now, while there is
// one caller, instead of becoming an `if` somebody has to find
// and invert later. Hardcore reads this per-mode; today it is
// one constant.
const RESUME_IN_PLACE = false;

// Real characters, never a bare \u escape in a JSX text child —
// JSX children are raw text and it renders the backslash.
const GLYPH = {
  diamond: "\u25c6", diamondOpen: "\u25c7", diamondDot: "\u25c8",
  star4: "\u2726", star8: "\u2736", check: "\u2713", moon: "\u263e",
  rook: "\u265c", skull: "\u2620", dash: "\u2014", shield: "\u26e8",
};
const UNKNOWN_CHILD = `${GLYPH.diamond} ? ? ? ${GLYPH.diamond}`;

// Pin faces are a size DOWN from M10 across the board. The old rig
// drew pins onto generated ground that had no detail of its own to
// compete with; the painting is dense everywhere, so a pin's job
// changed from "be the landmark" to "mark the landmark that is
// already drawn." Big discs cover the art they are pointing at.
const KIND_FACE = {
  gate:   { glyph: GLYPH.diamondDot,  size: 30, label: "Gateway" },
  battle: { glyph: GLYPH.diamond,     size: 25, label: "Duel" },
  fort:   { glyph: GLYPH.rook,        size: 35, label: "Fortress" },
  ghost:  { glyph: GLYPH.moon,        size: 27, label: "Hollow" },
  vault:  { glyph: GLYPH.diamondOpen, size: 27, label: "Lord's Chamber" },
  warp:   { glyph: GLYPH.star4,       size: 31, label: "The Seam" },
  // ⚠ `star` said "The Silver Lining" — a REGION that the 2026-08-21
  // re-trace deleted. It is the Pupil's face now, and there is exactly
  // one of them in the world.
  star:   { glyph: GLYPH.star8,       size: 27, label: "The Pupil" },
  child:  { glyph: GLYPH.star4,       size: 34, label: "Escling" },
  throne: { glyph: GLYPH.skull,       size: 40, label: "The Crimson Jewel" },
};
const faceFor = (k) => KIND_FACE[k] || KIND_FACE.battle;

// ═══════════════════════════════════════════════════════════════
// GEOMETRY — polylines in MAP space
// ═══════════════════════════════════════════════════════════════
// Both the drawn road and the walking avatar consume the SAME
// point list out of `routePoints`, which is the only reason a
// bent road and the doll travelling it can never disagree.

function measure(pts) {
  const seg = [];
  let total = 0;
  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1], b = pts[i];
    const L = Math.hypot(b.x - a.x, b.y - a.y);
    seg.push({ a, b, L, at: total });
    total += L;
  }
  return { seg, total };
}

function pointAt(pl, d) {
  if (!pl.seg.length) return null;
  if (d <= 0) { const s = pl.seg[0]; return { x: s.a.x, y: s.a.y, dx: s.b.x - s.a.x, dy: s.b.y - s.a.y }; }
  for (const s of pl.seg) {
    if (d <= s.at + s.L) {
      const t = s.L ? (d - s.at) / s.L : 1;
      return { x: s.a.x + (s.b.x - s.a.x) * t, y: s.a.y + (s.b.y - s.a.y) * t,
               dx: s.b.x - s.a.x, dy: s.b.y - s.a.y };
    }
  }
  const s = pl.seg[pl.seg.length - 1];
  return { x: s.b.x, y: s.b.y, dx: s.b.x - s.a.x, dy: s.b.y - s.a.y };
}

// A road with waypoints wants to READ as a road, which means it
// rounds its corners. Two points is a straight line; three or more
// bows through each waypoint on quadratics anchored at the segment
// midpoints — the standard smoothing, and it never overshoots a
// waypoint, which a Catmull-Rom would.
function pathD(pts) {
  if (pts.length < 2) return "";
  if (pts.length === 2) return `M${pts[0].x} ${pts[0].y} L${pts[1].x} ${pts[1].y}`;
  let d = `M${pts[0].x} ${pts[0].y}`;
  for (let i = 1; i < pts.length - 1; i++) {
    const m = { x: (pts[i].x + pts[i + 1].x) / 2, y: (pts[i].y + pts[i + 1].y) / 2 };
    d += ` Q${pts[i].x} ${pts[i].y} ${m.x} ${m.y}`;
  }
  const last = pts[pts.length - 1];
  return `${d} L${last.x} ${last.y}`;
}

// ═══════════════════════════════════════════════════════════════
// ARROW ASSIGNMENT — four keys, spent well
// ═══════════════════════════════════════════════════════════════
// up, down, left, right — MAP space, so y grows downward.
const ARROW_VECS = [[0, -1], [0, 1], [-1, 0], [1, 0]];

// How badly a road may fit the arrow it gets handed. Negative on
// purpose: a floor of 0 (never assign a road that leans away from
// the key) strands seven roads, because at a stop with two exits
// both leaning east, only one can have RIGHT and the other's other
// three arrows all score below zero. -0.35 buys those back, and
// because the solver maximises COUNT before fit it only ever
// reaches down here when a road would otherwise get nothing.
// Measured worst assignment across all 59 stops: -0.04. Nothing on
// this map ever hands you an arrow that walks you backwards.
const ARROW_FLOOR = -0.35;

// Exits in, four slots out. Maximise how many exits get an arrow;
// break ties on total fit. Brute force, and it should stay brute
// force: the widest stop in the world has five walk exits, so the
// search is at most a few hundred leaves, memoised per stop, and a
// real assignment algorithm here would be more code to read for
// the same answer.
function assignArrows(exits) {
  const out = [null, null, null, null];
  if (!exits.length) return out;

  const used = new Array(exits.length).fill(false);
  const cur  = [null, null, null, null];
  let best = out, bestCount = -1, bestScore = -Infinity;

  const rec = (ai, count, score) => {
    if (ai === 4) {
      // COUNT FIRST. This ordering is the entire fix — scoring on
      // fit alone will happily drop an exit when two good pairings
      // outscore three mediocre ones, and a dropped exit is a road
      // the keyboard cannot walk.
      if (count > bestCount || (count === bestCount && score > bestScore)) {
        bestCount = count; bestScore = score; best = cur.slice();
      }
      return;
    }
    rec(ai + 1, count, score);            // spend this arrow on nothing
    for (let e = 0; e < exits.length; e++) {
      if (used[e]) continue;
      const v = exits[e].s[ai];
      if (v < ARROW_FLOOR) continue;
      used[e] = true; cur[ai] = exits[e].id;
      rec(ai + 1, count + 1, score + v);
      used[e] = false; cur[ai] = null;
    }
  };
  rec(0, 0, 0);
  return best;
}

// ═══════════════════════════════════════════════════════════════
// THE STYLESHEET
// ═══════════════════════════════════════════════════════════════
// Injected imperatively with an id-guard AND a textContent compare.
// The compare is the load-bearing half: a stale <style> left by a
// previous build has the right id, so an id-only guard silently
// keeps the OLD keyframes and the new ones never resolve.
const WM_CSS = `
@keyframes wmBob{0%,100%{transform:translateY(0)}50%{transform:translateY(-3px)}}
@keyframes wmHop{0%,100%{transform:translateY(0) scaleY(1)}38%{transform:translateY(-7px) scaleY(1.035)}70%{transform:translateY(0) scaleY(.985)}}
@keyframes wmRipple{0%{transform:scale(.7);opacity:.6}100%{transform:scale(1.5);opacity:0}}
@keyframes wmPulse{0%,100%{opacity:.55}50%{opacity:1}}
@keyframes wmDashRun{to{stroke-dashoffset:-64}}
/* ☾ THE MOON'S AURA. Runs only while the door is affordable and
   unpaid — see THE MOON in Chrome. Border and shadow together, so
   it reads as light coming OFF the thing rather than a flashing
   button, which is what "aura etc so its obvious" asked for. */
@keyframes wmMoonPulse{0%,100%{box-shadow:0 0 0 rgba(255,34,102,0),inset 0 0 0 rgba(255,34,102,0)}50%{box-shadow:0 0 26px rgba(255,34,102,.55),inset 0 0 18px rgba(255,34,102,.18)}}
@keyframes wmGrainShift{0%{transform:translate(0,0)}20%{transform:translate(-3%,2%)}40%{transform:translate(2%,-3%)}60%{transform:translate(-2%,-2%)}80%{transform:translate(3%,1%)}100%{transform:translate(0,0)}}

/* ── THE ROADS ────────────────────────────────────────────────
   Deliberately THIN. The painting already has roads on it, drawn
   by hand, and a fat stroke over the top reads as defacement
   rather than as an overlay. Every state is opacity or filter;
   nothing animates a layout property.
     RED  = a road you walk, solid, routed along the painting
     YELLOW = a link you take, broken, always straight
     vis 0  hidden       (shipping only — see SHOW_ALL_ROUTES)
     vis x  revealed     (alignment mode: a route the player
                          could not normally see yet)
     vis 1  known-of     (the far side of the map)
     vis 2  live         (touching ground you have reached) */
.wmRd{transition:opacity .35s ease,filter .2s ease}
.wmRd[data-vis="0"]{opacity:0}
.wmRd[data-vis="x"]{opacity:.16}
.wmRd[data-vis="1"]{opacity:.3}
.wmRd[data-vis="2"]{opacity:.92}
.wmRd[data-route="1"]{filter:drop-shadow(0 0 6px rgba(255,214,150,.95)) drop-shadow(0 0 14px rgba(255,180,90,.5))}
.wmWarp{stroke-dasharray:7 9;animation:wmDashRun 1.4s linear infinite}
.wmSecret{stroke-dasharray:2 7}

/* ── THE PINS ─────────────────────────────────────────────────
   A pin is CENTRED on its coordinate, not hung above it, because
   the coordinate is a painted pad on the map and the disc's job is
   to sit in it.

   THE NAMEPLATES ARE GONE (caP'n, July 29: "get rid of all the
   little name tags, they clutter up the screen, don't need em").
   Fifty-nine of them, each one a bordered slab of vellum sitting
   on top of hand-painted artwork, and in the Marsh — twenty stops
   in one region — they overlapped each other AND the roads they
   were labelling. The painting already names its own places; the
   panel names whichever stop you have selected; and walking
   selects what you land on, so the name of where you are is
   always on screen exactly once. Three sources of the same fact
   was two too many, and the one being deleted is the one drawn
   over the art. */
.wmPin{position:absolute;transform:translate(-50%,-50%);display:grid;place-items:center;
  cursor:pointer;filter:drop-shadow(0 2px 5px rgba(0,0,0,.9));
  transition:transform .2s cubic-bezier(.2,.9,.2,1),opacity .35s,filter .35s}
.wmPin:hover,.wmPin[data-sel="1"]{transform:translate(-50%,-50%) scale(1.16);z-index:20}
/* A stop you have not reached should read as ABSENCE, not as an
   object. Locked stops are a small dim mark: no disc, no ring, no
   glyph, no label, no lift on hover. The eye goes straight to the
   road you can actually walk — which matters more here than it did
   on the generated map, because the painting is busy. */
.wmPin[data-st="locked"]{opacity:.42;filter:none}
.wmPin[data-st="locked"]:hover{opacity:.85;transform:translate(-50%,-50%)}
/* M10 — a keyboard has to be able to SEE where it is. focus-visible
   only, so a mouse tap never leaves a ring behind; a bright halo rather
   than the browser default outline, which disappears against a painted
   map. Locked pins are focusable ON PURPOSE: "this road is not open
   yet" is information, and a keyboard player who can only reach the
   open pins cannot learn the shape of the world. */
.wmPin:focus{outline:none}
.wmPin:focus-visible{outline:none;transform:translate(-50%,-50%) scale(1.16);z-index:30}
.wmPin:focus-visible::after{content:"";position:absolute;inset:-10px;border-radius:50%;
  border:2px solid #ffe7b0;box-shadow:0 0 0 2px rgba(10,6,20,.9),0 0 16px rgba(255,231,176,.75);
  pointer-events:none}
.wmPin[data-st="locked"]:focus-visible{opacity:.95}

/* ── THE CAST ─────────────────────────────────────────────────
   Flat sprite, flat shadow, one ground. With the tilt gone there
   is nothing to counter-rotate — a billboard is just an element. */
.wmPaper{position:absolute;transform:translate(-50%,-100%);
  pointer-events:none;filter:drop-shadow(0 2px 5px rgba(0,0,0,.75))}
.wmShadow{position:absolute;transform:translate(-50%,-50%);border-radius:50%;
  pointer-events:none;
  background:radial-gradient(closest-side,rgba(0,0,0,.72),rgba(0,0,0,.26) 62%,transparent 78%)}
/* ── THE GRADE ────────────────────────────────────────────────
   Kept, but at roughly HALF the strength it carried on the
   generated map, and that reduction is a real decision rather
   than timidity. M10's grade was doing corrective work: the
   procedural terrain measured ~9% mean luminance against a 0%
   void and needed bloom and vignette to stop reading as a smudge.
   The painting arrives already graded — it has its own vignette,
   its own colour temperature, its own atmosphere painted in.
   Stacking a full second grade on top of a finished one just
   mutes it. What is left is the frame furniture: a whisper of
   bloom, a vignette to hold the edges, and grain, which is the
   one layer that still earns its keep because it binds our
   vector pins to the painted surface underneath them.
   No blend modes, no blur — see the M10 note on why those cost
   the entire frame budget over a composited subtree. */
.wmGrade{position:absolute;inset:0;z-index:8;pointer-events:none}
.wmBloom{position:absolute;inset:0;
  background:radial-gradient(46% 34% at 52% 4%,rgba(255,203,220,.10),rgba(255,72,130,.035) 48%,transparent 76%)}
.wmVig{position:absolute;inset:0;
  background:radial-gradient(140% 118% at 50% 42%,transparent 46%,rgba(4,2,10,.22) 74%,rgba(2,1,6,.62) 100%)}
.wmGrain{position:absolute;left:-50%;top:-50%;width:200%;height:200%;opacity:.038;
  animation:wmGrainShift .55s steps(1) infinite;will-change:transform;
  background-image:url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='180' height='180'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3' stitchTiles='stitch'/></filter><rect width='180' height='180' filter='url(%23n)' opacity='0.9'/></svg>")}

/* THE VEIL. No longer opaque. It used to be flat #020107 because
   there was nothing behind it worth showing; now the player's
   equipped backdrop is back there, so the loading state is their
   own cosmetic breathing under a scrim rather than a black hole
   with a word in it. Still dark enough that a half-decoded
   painting never flashes through. */
.wmVeil{position:absolute;inset:0;z-index:30;pointer-events:none;
  background:radial-gradient(120% 100% at 50% 45%,rgba(2,1,7,.86),rgba(2,1,7,.96));
  transition:opacity 1s ease .1s}
`;
function ensureWorldMapCss() {
  if (typeof document === "undefined") return;
  const prev = document.getElementById("wm-keyframes");
  // textContent compare, not just an id-guard — see the note above.
  if (prev && prev.textContent === WM_CSS) return;
  if (prev) prev.remove();
  const el = document.createElement("style");
  el.id = "wm-keyframes"; el.textContent = WM_CSS;
  document.head.appendChild(el);
}
ensureWorldMapCss();

// ═══════════════════════════════════════════════════════════════
// THE SCREEN
// ═══════════════════════════════════════════════════════════════

export default function WorldMap({
  playerName, uid, profile, deck, result, onClearResult, onFight, onAmbush, ambushBlocked, onExit,
}) {
  const companion = useEquippedCompanion(uid);

  const [progress, setProgress] = useState(null);
  // ── THE ART GATE ────────────────────────────────────────────
  // Three states, not a boolean, and the third one is the reason.
  // A wrong asset path on this CDN does not 404 politely — the app
  // domain answers 200 with an HTML document, and even the asset
  // domain's miss is invisible to an <img> beyond `onerror` firing.
  // With a boolean, both outcomes read as "done" and the screen
  // renders pins floating in a black void with nothing anywhere
  // saying why. That is an hour of debugging the wrong layer.
  // "failed" is a real state that gets to speak.
  const [artState, setArtState] = useState("loading");
  const artReady = artState !== "loading";
  const [at, setAt]             = useState(START_NODE);
  const [selected, setSelected] = useState(null);
  const [nudge, setNudge]       = useState("");
  // ⭐ WHERE THE EYE-DOOR CAME FROM. See THE WAY BACK, below the pupil
  // pill. Null unless the player is standing in the pupil having
  // stepped there through an answered riddle.
  const [eyeFrom, setEyeFrom]   = useState(null);
  const [walking, setWalking]   = useState(false);
  const [raised, setRaised]     = useState(false);
  // ⬡ THE POP WINDOWS. A QUEUE, not a boolean — a single clear can be
  // a Sol Shard AND an answered riddle AND a trace of a child, and the
  // ribbon can only ever say the rarest one. See WorldNotice.js.
  const [notices, setNotices]   = useState([]);

  const wrapRef   = useRef(null);
  const camRef    = useRef(null);
  const planeRef  = useRef(null);
  const ptrs      = useRef(new Map());   // live pointers, for pinch
  const pinchRef  = useRef(null);
  const framedRef = useRef(false);       // has the You framing happened yet
  const atRef     = useRef(START_NODE);
  const avatarRef = useRef(null);
  const shadowRef = useRef(null);
  const rafRef    = useRef(0);
  const camState  = useRef({ x: 0, y: 0, z: 1 });
  const dragRef   = useRef(null);
  const facingRef = useRef({ x: 0, y: 1 });
  const [facing, setFacing]       = useState({ x: 0, y: 1 });
  const [walkFrame, setWalkFrame] = useState(false);

  const gender = profile?.gender === "f" ? "f" : "m";

  // ⬡ PUSH ONE WINDOW. Null-safe so a builder that decides there is
  // nothing to say can just return null.
  const pushNotice = useCallback((n) => {
    if (n) setNotices((q) => [...q, n]);
  }, []);
  const popNotice = useCallback(() => setNotices((q) => q.slice(1)), []);

  // ── SAVE DATA ───────────────────────────────────────────────
  useEffect(() => {
    let alive = true;
    loadWorldProgress(uid).then((p) => {
      if (!alive) return;
      setProgress(p);
      const here = NODE_BY_ID[p.at] ? p.at : START_NODE;
      setAt(here); setSelected(here);
    });
    return () => { alive = false; };
  }, [uid]);

  useEffect(() => {
    if (!result || !progress) return;
    if (!result.firstClear && !result.firstSecret && !result.child && !result.shards) return;
    setProgress((p) => ({
      ...p,
      cleared: result.nodeId ? { ...p.cleared, [result.nodeId]: Date.now() } : p.cleared,
      secrets: result.firstSecret && result.nodeId ? { ...p.secrets, [result.nodeId]: Date.now() } : p.secrets,
      found:   result.child ? { ...p.found, [result.child]: Date.now() } : p.found,
      // Shards ride the same optimistic patch as everything else.
      // Without this the header keeps reading the pre-duel count
      // until the screen is closed and reopened, and a player who
      // just beat a Sol King watching the number NOT move is
      // going to assume it broke.
      shards:  (result.shards && result.nodeId)
        ? { ...p.shards, [result.nodeId]: Date.now() }
        : p.shards,
    }));
  }, [result]);  // eslint-disable-line react-hooks/exhaustive-deps

  // ⬡ THE PAYOUT, IN WINDOWS. The ribbon ranks and keeps one; this
  // keeps them all. Guarded on the result OBJECT so a re-render cannot
  // re-queue the same win — `result` is replaced wholesale by App, and
  // a repeat clear of the same node is a new object.
  const noticedRef = useRef(null);
  useEffect(() => {
    if (!result || noticedRef.current === result) return;
    noticedRef.current = result;
    const built = noticesFor(result, NODE_BY_ID[result.nodeId]?.name || null);
    if (built.length) setNotices((q) => [...q, ...built]);
  }, [result]);

  const ctx       = useMemo(() => buildCtx({ progress, deck, profile, companionId: companion?.companionId }),
                            [progress, deck, profile, companion?.companionId]);

  // ═══════════════════════════════════════════════════════════
  // ⬡ THE PRICE — what the Veil eats, per crept step
  // ═══════════════════════════════════════════════════════════
  // caP'n: *"the price for such a powerful tool..s! plural!... You
  // know what D'Aeternuz's feed upon, I need not explain"* — and the
  // vault does say it: an A' is paid in AENERGY, *"forever transferred
  // or kept on standby, takeable at any moment."* Which Aenergies, and
  // how much, are rows in `vaelsTools.js`; this only counts them.
  //
  // ⚠ IT DOES NOT BITE A DUEL YET, ON PURPOSE. The debt is recorded
  // and shown; nothing reads it back into Anima or Khao. That wiring
  // is a rules change and it is caP'n's to call — see the banner in
  // vaelsTools.js. What this proves today is the seam: the tool is
  // held, the pace is slow, the price accrues, and it survives a
  // reload.
  //
  // Optimistic locally + one write, same shape as every other stamp on
  // this screen. A failed write costs the player nothing (it is a
  // debt) and must never cost them the step they just walked.
  const chargeVeil = useCallback((steps = 1) => {
    if (!progress || !steps) return;
    const cost = stepCost(progress);
    if (!Object.keys(cost).length) return;    // nothing on you that feeds
    const next = addSteps(progress.veil, cost, steps);
    setProgress((p) => ({ ...p, veil: next }));
    saveVeilLedger(uid, next);
  }, [progress, uid]);

  // ═══════════════════════════════════════════════════════════
  // ⬡ THE INTERCEPTION — the eighth shard is a doorbell
  // ═══════════════════════════════════════════════════════════
  // caP'n, 2026-08-20: *"if you lose, you get sent to plaza and
  // immediately upon re-entry it starts again."*
  //
  // Which is why this is an EFFECT ON CTX and not a branch inside
  // the clear handler. Firing it where the shard is earned would
  // handle the first time and nothing else; a player who lost would
  // walk back into a Minerium with an unstamped ambush and never see
  // it again. Watching `ctx` instead means it fires on the load that
  // follows a loss, on the clear that earns the eighth shard, and on
  // nothing else — because `recordAmbushClear` stamps on a win and
  // `pendingAmbush` reads that stamp.
  //
  // ⚠ THE GUARD IS A REF, NOT STATE. `onAmbush` unmounts this screen
  // (App swaps to the duel), but React may re-render once more before
  // it does, and `ctx` is a fresh object on every progress patch. A
  // state flag would race that; a ref is written synchronously and is
  // read by the same render that wrote it.
  // ⚠ THE DOOR CLOSES BEHIND YOU. `eyeFrom` was cleared only by USING
  // the Back pill — so a player who stepped into the Eye, walked out
  // the ordinary way, and came back to the pupil months later found the
  // pill still offering a free one-way warp to a doorstep they were
  // nowhere near. A door remembers the room it opened from for exactly
  // as long as you are standing in the doorway.
  useEffect(() => {
    if (at !== EYE_NODE) setEyeFrom((prev) => (prev === null ? prev : null));
  }, [at]);

  const ambushFiredRef = useRef(false);
  useEffect(() => {
    if (!progress || !onAmbush) return;
    if (ambushFiredRef.current) return;
    // ⚠ NEVER OVER THE TOP OF A CUTSCENE. The eighth shard lands on
    // the very clear whose aftermath scene is opening — App sets the
    // boss's onWin lines and this effect would call setMapScene one
    // frame later and throw them away. So the interception waits for
    // the stage, and the effect re-runs when it clears because the
    // flag is in its dependency array.
    if (ambushBlocked) return;
    const a = pendingAmbush(progress, ctx);
    if (!a) return;
    const foe = opponentForAmbush(a);
    if (!foe) { console.warn("[worldMap] ambush", a.id, "has no opponent — skipped"); return; }
    ambushFiredRef.current = true;
    onAmbush(a, foe);
  }, [progress, ctx, onAmbush, ambushBlocked]);
  const reachable = useMemo(() => reachableSet(ctx), [ctx]);
  const regions   = useMemo(() => discoveredRegions(ctx, reachable), [ctx, reachable]);
  const neighbors = useMemo(() => neighborsOf(at, ctx), [at, ctx]);
  const kidsFound = useMemo(() => childrenFound(ctx), [ctx]);
  // ☾ ◉ Both things in the sky you can press, in one list. See
  // SKY_DOORS in worldData — the Moon and the Eye are one mechanism.
  const skyDoors  = useMemo(() => skyDoorStates(ctx), [ctx]);
  // ☾ ◉ Where each sky door picked you up, BY DOOR, read off the save.
  // A door is a door both ways, and the Crimson Dais has no road home —
  // see saveSkyFrom in worldData for the strand this replaced. Local
  // state would not survive the remount that happens after every duel.
  const skyFrom = progress?.skyFrom || {};
  const rememberSky = useCallback((doorId, nodeId) => {
    setProgress((prev) => ({ ...prev, skyFrom: { ...(prev?.skyFrom || {}), [doorId]: nodeId } }));
    saveSkyFrom(uid, doorId, nodeId);
  }, [uid]);

  // ── ⬡ WHICH PAINTING ────────────────────────────────────────
  // Derived from the pawn, not held in state. A save that ends in
  // another Minerium reopens there and never flashes Minerium 1
  // first, and there is no second source of truth to get out of step
  // with `at`. Today every node belongs to Minerium 1, so `wmap` IS
  // `MAP`, `worldNodes` IS `NODES` and `worldPaths` IS `PATHS` — this
  // whole block is a no-op until a second painting exists, which is
  // exactly what makes it safe to land without one.
  const worldId    = worldOfNode(at);
  const wmap       = mapOf(worldId);
  const worldNodes = useMemo(() => nodesIn(worldId), [worldId]);
  const worldPaths = useMemo(() => pathsIn(worldId), [worldId]);
  // ⬡ OPEN SEAM, deliberately unread: worldData.mineriumName(worldId) is the
  // label for "which Minerium am I standing in," and the Chrome bar
  // is where it goes the day there are two. Not wired now because
  // caP'n has not asked for the label, and a chrome change nobody
  // requested is a change nobody can review.


  // ── THE UN-STRAND ───────────────────────────────────────────
  // Runs ONCE, on the first render that has real save data. It
  // cannot run inside the load effect above, because reachability
  // is derived from `progress` and `progress` is being set by that
  // very callback — the set it would need does not exist yet at
  // the moment it would have to ask. So the check waits one render
  // for `ctx` and `reachable` to catch up, which they do in the
  // same commit React batches the load into.
  //
  // The ref is what makes this a LOAD-TIME rule rather than a
  // standing one. Reachability only ever grows during play, so a
  // standing version would be harmless today — and would quietly
  // become a teleport the first time any mode takes a road away
  // mid-session. Once, on arrival, is the whole contract.
  //
  // Surveyor is exempt for the obvious reason: nothing is ever
  // unreachable in Surveyor, so the check has no opinion, and
  // yanking the instrument back to the start of the map would
  // defeat the one job the instrument has.
  const unstrandRef = useRef(false);
  useEffect(() => {
    if (!progress || unstrandRef.current) return;
    unstrandRef.current = true;
    if (SURVEYOR || RESUME_IN_PLACE) return;
    if (reachable.has(at) || ctx.cleared.has(at)) return;
    setAt(START_NODE);
    setSelected(START_NODE);
    saveWorldPosition(uid, START_NODE);
    setNudge("The road back has closed. You are at The Roadhead.");
  }, [progress, reachable, ctx, at, uid]);

  // ── THE ROADS, BUILT ONCE ──────────────────────────────────
  // Geometry only. None of it depends on save state, so none of it
  // re-runs when save state moves — run state is applied afterwards
  // by setting attributes on paths that already exist in the DOM.
  const roads = useMemo(() => worldPaths.map((p) => {
    // WALK vs TAKE, decided once, here. `via` is the only input —
    // a route's colour is a fact about how you use it, not about
    // where it happens to be drawn.
    // ⚑ YELLOW MEANS BEACON (caP'n, Aug 17: "nodes never connect to
    //   other nodes with the yellow dashed line"). A secret used to
    //   share the yellow family because both are "a link you take
    //   rather than a road you walk" — but on screen that put a
    //   yellow dash between two ordinary nodes, which is now the one
    //   thing yellow is not allowed to mean. A secret is a road; it
    //   just has to be found first. So it draws and hides WITH the
    //   roads, and yellow is reserved for the ten beacons.
    const warp = p.via === "warp";
    return {
      key:  edgeKey(p),
      d:    pathD(routePoints(p, true)),
      take: warp,
      warp,
      secret: p.via === "secret",
      hue:  warp ? ROUTE_TAKE : ROUTE_WALK,
    };
  }).filter((r) => r.d), []);

  // ── CAMERA ──────────────────────────────────────────────────
  // Live viewport, measured off the wrap rather than the window —
  // the screen is `inset:0` inside whatever the app shell gives it,
  // and on a letterboxed stage those two numbers are not the same.
  const viewport = useCallback(() => {
    const wrap = wrapRef.current;
    const r = wrap && wrap.getBoundingClientRect();
    return {
      w: (r && r.width)  || (typeof window !== "undefined" ? window.innerWidth  : 1280),
      h: (r && r.height) || (typeof window !== "undefined" ? window.innerHeight : 720),
    };
  }, []);

  // THE ONLY PLACE A TRANSFORM IS WRITTEN, and therefore the only
  // place the Frame Law needs to be enforced. Callers set whatever
  // they like on camState; this decides what is legal.
  const applyCam = useCallback(() => {
    const c = clampCamTo(camState.current, viewport(), wmap);
    if (camRef.current) camRef.current.style.transform = `translate(${c.x}px,${c.y}px) scale(${c.z})`;
  }, [viewport, wmap]);

  // Centre on a MAP coordinate. With the tilt gone this is honest
  // arithmetic rather than the old read-the-rect trick — a node's
  // screen position is now exactly `coord * z + pan`, so there is
  // nothing to measure and it works before first paint.
  //
  // It may ASK for a framing that the Frame Law will not allow —
  // a node near the painting's edge cannot actually be centred
  // without showing the void past it. That is fine and deliberate:
  // this function's job is to say where the camera would like to
  // be, and `applyCam` decides where it is permitted to be. Every
  // map in every game behaves this way at the edges.
  const centerOnXY = useCallback((mx, my, zoom, smooth = true) => {
    const wrap = wrapRef.current; if (!wrap) return;
    const h = wrap.getBoundingClientRect();
    const z = zoom || camState.current.z;
    if (smooth && camRef.current) {
      camRef.current.style.transition = "transform .75s cubic-bezier(.2,.8,.2,1)";
      setTimeout(() => { if (camRef.current) camRef.current.style.transition = ""; }, 800);
    }
    camState.current = { z, x: h.width / 2 - mx * z, y: h.height * 0.46 - my * z };
    applyCam();
  }, [applyCam]);

  const centerOn = useCallback((nodeId, zoom, smooth = true) => {
    const n = NODE_BY_ID[nodeId]; if (!n) return;
    centerOnXY(n.mx, n.my, zoom, smooth);
  }, [centerOnXY]);


  // ── THE DIAL, ON SCREEN  (August 17, 2026) ──────────────────
  // Wheel and pinch have always existed. They are also invisible,
  // undiscoverable on a laptop with no trackpad gesture, and gone
  // entirely on a phone that only ever gets one thumb on the glass.
  // A camera the player cannot see the controls for is a camera
  // they will assume is broken — and this session, for a different
  // reason, it actually was. So the dial gets a face.
  //
  // Zooms about the CENTRE of the frame rather than the cursor,
  // which is the correct anchor for a button: there is no pointer
  // position to honour, and the thing the player is looking at when
  // they reach for a +/- is the middle of the screen. Same focal
  // maths as the pinch handler, with (px,py) fixed at the centre.
  const zoomBy = useCallback((factor) => {
    const wrap = wrapRef.current; if (!wrap) return;
    const r = wrap.getBoundingClientRect();
    const c = camState.current;
    const vp = { w: r.width, h: r.height };
    const nz = Math.min(zCeilFor(vp, wmap), Math.max(zFloorFor(vp, wmap), c.z * factor));
    if (nz === c.z) return;
    const px = r.width / 2, py = r.height / 2;
    if (camRef.current) {
      camRef.current.style.transition = "transform .28s cubic-bezier(.2,.8,.2,1)";
      setTimeout(() => { if (camRef.current) camRef.current.style.transition = ""; }, 320);
    }
    camState.current = {
      z: nz,
      x: px - (px - c.x) * (nz / c.z),
      y: py - (py - c.y) * (nz / c.z),
    };
    applyCam();
  }, [applyCam, wmap]);

  // The whole painting, centred. Asks for the floor exactly; the
  // Frame Law's own clamp does the centring, because at the floor
  // the art is narrower than the frame on at least one axis and
  // clampCamTo centres anything narrower than its frame.
  const fitWorld = useCallback(() => {
    const wrap = wrapRef.current; if (!wrap) return;
    const r = wrap.getBoundingClientRect();
    centerOnXY(wmap.w / 2, wmap.h / 2, zFloorFor({ w: r.width, h: r.height }, wmap), true);
  }, [centerOnXY, wmap]);

  useEffect(() => {
    // Resize must not snap back to a world-fit framing — that is a
    // camera view of its own, and this screen only has one. Re-frame
    // on the player at whatever zoom they were already using.
    // A resize also changes what the Frame Law permits — a window
    // dragged wider raises the cover floor, and a camera that was
    // legal a frame ago can be illegal now. `applyCam` re-clamps
    // unconditionally, so the bare call is not a no-op.
    const onResize = () => {
      if (framedRef.current) centerOn(atRef.current, camState.current.z, false);
      else applyCam();
    };
    window.addEventListener("resize", onResize);
    const t = setTimeout(applyCam, 50);
    return () => { window.removeEventListener("resize", onResize); clearTimeout(t); };
  }, [applyCam, centerOn]);

  // Once save data AND the painting have both landed, frame the
  // player and lift the veil. Waiting on the image is the point —
  // raising on save data alone shows a black rectangle with pins
  // floating in it for however long the CDN takes.
  useEffect(() => {
    if (!progress || !artReady) return;
    // Opens on the WIDEST LEGAL SHOT — the whole frame full of
    // painting, player centred. 0.85 used to be a magic number
    // here and it meant something different on every machine;
    // the floor means the same thing on all of them.
    // ⚠ `atRef.current`, NOT `at`. This effect fires the moment save
    // data lands, and THE UN-STRAND can move the player on that very
    // commit — after this closure has already captured the old stop.
    // Reading the ref at fire time means the opening shot always
    // frames wherever the doll actually ended up. The resize handler
    // above already reads the ref for the same reason; the two
    // camera jumps now agree, which is the point of `ie-room-chrome`'s
    // rule that a camera function and its partner are edited together.
    const t = setTimeout(() => {
      centerOn(atRef.current, zFloorFor(viewport(), wmap), false);
      framedRef.current = true;
      setRaised(true);
    }, 120);
    return () => clearTimeout(t);
  }, [progress, artReady]);  // eslint-disable-line react-hooks/exhaustive-deps

  // ── PAN / ZOOM ──────────────────────────────────────────────
  // One finger pans, two fingers pinch, wheel zooms. The rotate and
  // tilt gestures are GONE with the rig that needed them — there is
  // no meaningful "spin" of a painted map, and a tilt control would
  // only ever let the player break the perspective the artist drew.
  // Zoom is clamped HERE too, not only in applyCam, and that is not
  // redundant: the pinch handler needs the legal `nz` up front to
  // compute the focal-point maths that keeps the pixel under your
  // fingers still. Clamping only afterwards would let the anchor
  // drift every time you push against the floor.
  const clampZ = useCallback((v) => {
    const vp = viewport();
    return Math.min(zCeilFor(vp, wmap), Math.max(zFloorFor(vp, wmap), v));
  }, [viewport, wmap]);

  const beginPinch = () => {
    const p = [...ptrs.current.values()]; if (p.length < 2) return;
    const [a, b] = p, c = camState.current;
    pinchRef.current = {
      dist: Math.max(1, Math.hypot(a.x - b.x, a.y - b.y)),
      z: c.z, cx: c.x, cy: c.y,
    };
    dragRef.current = null;   // two fingers cancel a pan
  };

  const onPointerDown = (e) => {
    if (e.target.closest(".wmPin") || e.target.closest("[data-chrome]")) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    ptrs.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (ptrs.current.size >= 2) { beginPinch(); return; }
    dragRef.current = { x: e.clientX, y: e.clientY, cx: camState.current.x, cy: camState.current.y, moved: 0 };
  };

  const onPointerMove = (e) => {
    if (ptrs.current.has(e.pointerId)) ptrs.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    const pin = pinchRef.current;
    if (pin && ptrs.current.size >= 2) {
      const [a, b] = [...ptrs.current.values()];
      const wrap = wrapRef.current; if (!wrap) return;
      const h = wrap.getBoundingClientRect();
      const dist = Math.max(1, Math.hypot(a.x - b.x, a.y - b.y));
      const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2;
      const nz = clampZ(pin.z * (dist / pin.dist));
      const px = mx - h.left, py = my - h.top;
      camState.current = { z: nz, x: px - (px - pin.cx) * (nz / pin.z), y: py - (py - pin.cy) * (nz / pin.z) };
      applyCam();
      return;
    }

    const d = dragRef.current; if (!d) return;
    d.moved = Math.max(d.moved, Math.abs(e.clientX - d.x) + Math.abs(e.clientY - d.y));
    camState.current.x = d.cx + (e.clientX - d.x);
    camState.current.y = d.cy + (e.clientY - d.y);
    applyCam();
  };

  const onPointerUp = (e) => {
    if (e && e.pointerId !== undefined) ptrs.current.delete(e.pointerId);
    if (ptrs.current.size < 2) pinchRef.current = null;
    if (ptrs.current.size === 0) dragRef.current = null;
  };

  // ── THE WHEEL, ATTACHED BY HAND ─────────────────────────────
  // THIS IS THE BUG THAT ATE THE CHROME. The handler used to be a
  // JSX `onWheel={...}` calling `e.preventDefault()`, and that
  // preventDefault has never once run: React 17+ registers `wheel`
  // (along with `touchstart` and `touchmove`) as a PASSIVE listener
  // on the root container, and a passive listener is by definition
  // forbidden from cancelling its event. The browser logs a console
  // warning nobody reads and carries on with the default action.
  //
  // On a trackpad the default action is the killer. A two-finger
  // pinch does not arrive as a touch event on desktop — it arrives
  // as a `wheel` with `ctrlKey` set, and the browser's default for
  // that is PAGE ZOOM. So a pinch on this screen zoomed the map and
  // the entire document at the same time, and the document zoom
  // pushed the title, the pills, the region list and the app's own
  // MENU straight off the visual viewport. The chrome was never
  // unmounted or hidden — it was off-screen, in a browser that was
  // zoomed to 250%, and nothing in this component could have caused
  // that or could have shown it again.
  //
  // The cure is to own the listener: `addEventListener` with
  // `{ passive: false }`, where preventDefault is real. `gesture*`
  // is Safari's separate, non-standard pinch channel and needs its
  // own swallow. And the window-level capture guard catches a
  // ctrl+wheel that lands on the app shell's MENU button, which is
  // outside this component's subtree and would otherwise still
  // zoom the page out from under the map.
  //
  // `progress` is in the deps on purpose: until save data lands
  // this component renders the OPENING THE WORLD placeholder and
  // `wrapRef.current` is null, so an effect that ran only once
  // would silently attach to nothing.
  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;

    const onWheelNative = (e) => {
      e.preventDefault();
      const h = wrap.getBoundingClientRect(), c = camState.current;
      // A pinch sends small, continuous ctrl+deltas; a wheel sends
      // big discrete notches. Same dial, two different hands on it.
      const k = e.ctrlKey ? Math.exp(-e.deltaY * 0.012)
                          : (e.deltaY < 0 ? 1.12 : 1 / 1.12);
      const nz = clampZ(c.z * k);
      const px = e.clientX - h.left, py = e.clientY - h.top;
      camState.current = { z: nz, x: px - (px - c.x) * (nz / c.z), y: py - (py - c.y) * (nz / c.z) };
      applyCam();
    };
    const swallow = (e) => e.preventDefault();
    const guardPageZoom = (e) => { if (e.ctrlKey) e.preventDefault(); };

    wrap.addEventListener("wheel", onWheelNative, { passive: false });
    wrap.addEventListener("gesturestart", swallow, { passive: false });
    wrap.addEventListener("gesturechange", swallow, { passive: false });
    wrap.addEventListener("gestureend", swallow, { passive: false });
    window.addEventListener("wheel", guardPageZoom, { passive: false, capture: true });

    return () => {
      wrap.removeEventListener("wheel", onWheelNative);
      wrap.removeEventListener("gesturestart", swallow);
      wrap.removeEventListener("gesturechange", swallow);
      wrap.removeEventListener("gestureend", swallow);
      window.removeEventListener("wheel", guardPageZoom, { capture: true });
    };
  }, [applyCam, clampZ, progress]);

  // ── RUN STATE, APPLIED TO THE DOM ───────────────────────────
  // The roads are never rebuilt. Each already carries its geometry;
  // this only decides whether it is lit, dim, revealed or absent.
  useEffect(() => {
    const wrap = wrapRef.current; if (!wrap) return;
    wrap.querySelectorAll(".wmRd[data-e]").forEach((g) => {
      const p = worldPaths.find((q) => edgeKey(q) === g.dataset.e);
      if (!p) return;
      const s = pathState(p, ctx);
      const seen = reachable.has(p.from) || ctx.cleared.has(p.from) || reachable.has(p.to);
      // A route the player is not meant to see yet is absent when
      // shipping and merely faint in alignment mode — never bright,
      // because even while checking a layout it should be obvious at
      // a glance which routes are the earned ones.
      g.setAttribute("data-vis",
        !s.visible ? (SHOW_ALL_ROUTES ? "x" : "0") : seen ? "2" : "1");
    });
  }, [ctx, reachable]);

  // Light the road the player is about to walk
  useEffect(() => {
    const wrap = wrapRef.current; if (!wrap) return;
    wrap.querySelectorAll(".wmRd[data-route]").forEach((g) => g.removeAttribute("data-route"));
    if (!selected || selected === at) return;
    const hop = neighbors.get(selected);
    if (!hop) return;
    const k = edgeKey(hop.path);
    wrap.querySelectorAll(`.wmRd[data-e="${k}"]`).forEach((g) => g.setAttribute("data-route", "1"));
  }, [selected, at, neighbors]);

  // ── THE WALK ────────────────────────────────────────────────
  // One step at a time along a real road, in MAP pixels. One rAF,
  // imperative on refs, no setState in the loop — the perf law,
  // same as the duel board. With the heightfield gone there is
  // nothing to sample under a footfall; the ground is the painting
  // and it is flat, which is the whole simplification.
  const placeAvatar = useCallback((x, y) => {
    const el = avatarRef.current;
    if (el) { el.style.left = `${x}px`; el.style.top = `${y}px`; }
    // THE CONTACT SHADOW. Still the half of the trick that sells it:
    // the doll's feet are at the coordinate, the shadow is centred
    // ON it, so the figure reads as standing on the ground rather
    // than as a sticker over it. Delete the shadow and the player
    // becomes a decal on the artwork.
    const sh = shadowRef.current;
    if (sh) { sh.style.left = `${x}px`; sh.style.top = `${y}px`; }
  }, []);

  // Park the avatar wherever `at` is, whenever we are not mid-walk.
  useEffect(() => {
    if (walking) return;
    const n = NODE_BY_ID[at]; if (!n) return;
    placeAvatar(n.mx, n.my);
  }, [at, walking, placeAvatar, artReady]);

  const step = useCallback((nodeId, opts = {}) => {
    const from = NODE_BY_ID[at], to = NODE_BY_ID[nodeId];
    if (!from || !to || walking) return;
    const hop = neighbors.get(nodeId);
    const path = hop?.path;
    setSelected(nodeId);
    setNudge("");

    const commit = () => {
      setWalking(false);
      setAt(nodeId);
      saveWorldPosition(uid, nodeId);
      setTimeout(() => centerOn(nodeId, null, true), 40);
    };
    // ⬡ WHAT A CREPT STEP COSTS. Charged on the WALK, never on the
    // cut — a warp pays nothing because the Veil was not carrying you
    // through it. See chargeVeil.
    const chargeWalk = () => chargeVeil(1);

    // A warp is not a walk — it is a cut. Nothing to animate along.
    //
    // ⬡ AND A CUT IS WHAT BREAKS THE VEIL. caP'n: *"as long as they
    // move at a snails pace they will remain undetected."* A cloak
    // hides a body moving through a place; a warp is a body that was
    // never in between, so there is nothing for it to hide. This is
    // THE TWO-COLOUR LAW doing a second job for free — red is a road
    // you WALK and stays covered, yellow is a link you TAKE and does
    // not. It costs nothing and it does not confiscate the Veil: it
    // slips, and the next road you walk closes it over you again.
    if (!path || opts.warp || path.via === "warp") {
      const broke = toolBrokenBy(progress, "warp");
      if (broke) pushNotice(noticeForBreak(broke));
      placeAvatar(to.mx, to.my);
      commit();
      return;
    }

    // `forward` matters: a road authored A→B with waypoints is
    // walked B→A in reverse, and routePoints reverses the list so
    // the doll bows through the same curve either direction.
    const pl = measure(routePoints(path, path.from === at));
    if (!pl.total) { placeAvatar(to.mx, to.my); chargeWalk(); commit(); return; }

    // ⬡ THE PACE. `paceFor` returns the slowest thing on you, or the
    // ordinary walk when nothing is. Resolved HERE rather than at the
    // top of the component so a tool won mid-session takes effect on
    // the very next step, with no remount and no stale closure.
    setWalking(true);
    const pace = paceFor(progress, STEP_SPEED);
    let d = 0, frameTick = 0;
    cancelAnimationFrame(rafRef.current);
    const tick = () => {
      d += pace;
      if (d >= pl.total) { placeAvatar(to.mx, to.my); chargeWalk(); commit(); return; }
      const p = pointAt(pl, d);
      placeAvatar(p.x, p.y);
      const f = Math.abs(p.dx) > Math.abs(p.dy)
        ? { x: p.dx > 0 ? 1 : -1, y: 0 }
        : { x: 0, y: p.dy > 0 ? 1 : -1 };
      if (f.x !== facingRef.current.x || f.y !== facingRef.current.y) {
        facingRef.current = f; setFacing(f);
      }
      // The doll's legs keep time with the pace, or a creeping
      // player moon-walks the whole Marsh.
      if (++frameTick % (pace < STEP_SPEED ? 22 : 9) === 0) setWalkFrame((w) => !w);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  }, [at, walking, neighbors, uid, placeAvatar, centerOn, progress, pushNotice, chargeVeil]);

  useEffect(() => () => cancelAnimationFrame(rafRef.current), []);

  // ── THE RULES, UNCHANGED ────────────────────────────────────
  // ── TAPPING SELECTS. IT NO LONGER WALKS. ────────────────────
  // caP'n: "only let me walk the appropriate path by using the
  // arrow keys." Movement is one input now, and a pin is a thing
  // you INSPECT. This also quietly removes a real hazard the
  // nameplates used to paper over: with the labels gone the discs
  // are small and close together in the Marsh, and a tap that
  // walked you somewhere you did not mean to go is a worse
  // mistake on a map than a mis-click anywhere else, because it
  // costs a step of real travel and a save write.
  //
  // Mouse-only walking is not lost — it moved to the panel, where
  // Travel is a deliberate press rather than a stray tap.
  const tapNode = useCallback((node) => {
    setSelected(node.id);
    setNudge("");
  }, []);

  // `ctxOverride` exists for exactly one caller — THE MOON, which
  // knows a truth about the world that this render does not yet. See
  // THE STALE-CTX TRAP there. Every other caller passes nothing and
  // gets the ordinary ctx.
  const enter = useCallback((node, ctxOverride) => {
    const useCtx = ctxOverride || ctx;
    const opponent = opponentForNode(node);
    const secret = secretOffered(node, useCtx);
    onClearResult && onClearResult();
    onFight && onFight(node, opponent, secret);
  }, [ctx, onFight, onClearResult]);

  // ═══════════════════════════════════════════════════════════
  // ☾ THE MOON — the only way onto the Crimson Dais
  // ═══════════════════════════════════════════════════════════
  // caP'n, 2026-08-20:
  //
  //   *"once you get 15, the moon in the top left of the Minerium
  //    becomes an interactable button that has aura etc so its
  //    obvious, then you click it and it teleports you to the middle
  //    big jewel in the middle, then the 2 phase boss fight begins."*
  //
  // This REPLACED a handler called `enterEmperium`, which paid the
  // door and then moved the CAMERA — it called `setSelected` and
  // `centerOn` and nothing else, so the player's pawn stayed exactly
  // where it had been standing and the "teleport" was a pan. Fifteen
  // Sol Shards bought a nice view of a place you were not in.
  //
  // ── THE THREE STATES, IN THE ORDER THEY ARE CHECKED ─────────
  //   shut & unaffordable → say the number and stop
  //   shut & affordable   → PAY · LAND · FIGHT, in that order
  //   already paid        → LAND. No fight. See below.
  //
  // ⭐ WHY AN ALREADY-PAID MOON DOES NOT RE-START THE FINAL. A loss
  // sends the player to the Plaza, and a Moon that re-launched the
  // duel on touch would make the Dais unreachable forever — the four
  // gems, their four eggs and their shards sit on the far side of a
  // fight you have already proved you cannot win yet. So the second
  // press is a lift, not a summons: it puts you on the Jewel, and
  // ENTER on the Jewel is how you knock again. The Dais is a place
  // after the first press and a threshold only once.
  // ═══════════════════════════════════════════════════════════
  // ☾ ◉ PRESSING A SKY DOOR
  // ═══════════════════════════════════════════════════════════
  // One handler for both, because the Moon and the Eye are the same
  // object with different conditions. See SKY_DOORS in worldData.
  //
  // ── THE FOUR STATES, IN THE ORDER THEY ARE CHECKED ──────────
  //   already through it → COME BACK, to wherever it picked you up
  //   shut               → say why, and stop
  //   shut & affordable  → PAY, then LAND
  //   already paid / free & open → LAND
  //
  // ⭐ THE MOON NO LONGER OPENS THE FINAL. Its first cut landed the
  // player on the Jewel and launched the two-phase fight in the same
  // press, which was correct while the Dais was one node. caP'n's
  // trace made it four wardens and a centre, and named the landing:
  // *"you will start on AZS-52."* So the Moon is the door onto the
  // dais, and the Jewel is at the end of a square you have to walk.
  //
  // ⚠ AND IT REFUSES DURING A WALK, because `step` opens with
  // `if (!from || !to || walking) return;` — pressing the Moon mid-
  // animation used to charge fifteen Sol Shards, permanently, and then
  // do nothing at all.
  const pressSky = useCallback(async (d) => {
    if (!d) return;
    if (walking) { setNudge("Not while you are on the road."); return; }
    const there = NODE_BY_ID[d.target];
    if (!there) { setNudge("There is nothing there."); return; }

    // ── WHICH SIDE OF IT AM I ON ─────────────────────────────
    // ⚠ THE DOOR DECIDES, NOT THIS FUNCTION. `homeFrom: "region"` for
    // the Moon, because the Crimson Dais has no road off it and
    // pressing the Moon anywhere on that square has to mean "home".
    // `homeFrom: "node"` for the Eye, because the Eyeland's ring IS
    // walkable and a player standing on it who presses a newly-lit Eye
    // means "let me IN". One shared region test got the second case
    // exactly backwards and made the Eye inert at the only moment
    // anybody would ever press it.
    const inThere = d.homeFrom === "node"
      ? at === d.target
      : NODE_BY_ID[at]?.region === there.region;

    if (inThere) {
      const back = skyFrom[d.id] && NODE_BY_ID[skyFrom[d.id]] ? skyFrom[d.id] : null;
      // ⚠ AND IT ALWAYS GOES SOMEWHERE. A door with no memory used to
      // say "You did not come this way" and do nothing — which on the
      // Dais is a soft-lock with a fifteen-shard entry fee. A forgotten
      // way home is a reason to walk out of the front door, not a
      // reason to stay in the room.
      const dest = back || START_NODE;
      rememberSky(d.id, null);
      step(dest, { warp: true });
      setNudge(back ? (d.back || "") : "The way you came is not where it was. You are put out at the Roadhead.");
      return;
    }

    // ── PAYING ───────────────────────────────────────────────
    if (d.emperium && !d.paid) {
      if (!d.live) { setNudge(`${Math.max(0, d.cost - d.held)} more Sol Shards.`); return; }
      const res = await openEmperium(uid, d.emperium);
      if (!res.ok) { setNudge(res.why || "It did not answer."); return; }
      // Optimistic, same as every other stamp on this screen.
      setProgress((prev) => ({
        ...prev,
        emperiums: { ...(prev?.emperiums || {}), [d.emperium]: { paid: d.cost, at: Date.now() } },
      }));
    } else if (!d.live) {
      setNudge(d.shut || "It is not open to you.");
      return;
    }

    // ── LANDING ──────────────────────────────────────────────
    rememberSky(d.id, at);
    step(d.target, { warp: true });
    setNudge(d.open || "");
  }, [uid, at, walking, skyFrom, step, rememberSky]);

  // ◉ THE EYE OPENS TO YOU — the notification caP'n asked for.
  // *"You'll get a notification as player saying the eye opens to you
  // and you can now click on it proper."* Fires once, on the render
  // after the sixth keeper falls (or the fourth Dais egg lands), and
  // never again — the ref is what stops it re-announcing on every
  // subsequent ctx rebuild, of which there are many.
  // ⚠ A REF DIES WITH THE MOUNT, AND THIS SCREEN UNMOUNTS FOR EVERY
  // DUEL. The first cut guarded with a `useRef`, which is correct
  // within one mount and useless across them — so the announcement
  // would have re-fired on every single return to the map, forever,
  // once the Eye was open. It is a BEAT now, stamped in the save
  // exactly like a story interlude, which is the mechanism this file
  // already has for "play this once, ever".
  const eyeToldRef = useRef(false);
  useEffect(() => {
    if (!progress || !uid) return;
    if (!eyeOpen(ctx)) return;
    if (eyeToldRef.current) return;
    if (progress.beats && progress.beats.eye_opened) { eyeToldRef.current = true; return; }
    eyeToldRef.current = true;
    setNudge("The Eye has opened to you. It can be entered from the sky now.");
    recordBeat(uid, "eye_opened").catch(() => {});
    setProgress((prev) => ({ ...prev, beats: { ...(prev?.beats || {}), eye_opened: Date.now() } }));
  }, [progress, ctx, uid]);


  const warpThrough = useCallback((node) => {
    for (const p of PATHS) {
      if (p.from !== node.id || p.via !== "warp") continue;
      if (!pathState(p, ctx).open) continue;
      step(p.to, { warp: true });
      return;
    }
    setNudge("It is shut.");
  }, [ctx, step]);

  // ═══════════════════════════════════════════════════════════
  // THE ARROW KEYS — one press, one hop, only down a real road
  // ═══════════════════════════════════════════════════════════
  // caP'n: "only let me walk the appropriate path by using the
  // arrow keys left right down up to the next node."
  //
  // ── WHY IT ASKS THE ROAD, NOT THE DESTINATION ──────────────
  // The obvious implementation compares the arrow against the
  // straight line from here to each neighbour. It is wrong on
  // this map, and wrong in a way that feels like a bug rather
  // than a preference: these roads BEND. `bend` waypoints exist
  // precisely because two pads sit on a road that curves between
  // them, and The Long Haul bows west around the Hive to reach a
  // stop that is due north. Judged by its destination that road
  // answers to UP, while the painting shows it leaving to the
  // LEFT — so you press up, and your doll walks off sideways.
  //
  // So the primary signal is the direction the road LEAVES in:
  // the first segment of the exact point list the avatar is about
  // to walk, out of `routePoints`, which is the same list the
  // drawn route uses. Press the way the road goes and you take
  // that road. The destination keeps a 25% say as a tie-break —
  // two roads leaving at 45° either side of north score
  // identically on departure alone, and the one that actually
  // ends up further north should win that coin flip.
  //
  // ── WHY IT IS AN ASSIGNMENT AND NOT A LOOKUP ───────────────
  // This is the part that had to be measured rather than
  // reasoned about, and the first version was quietly broken.
  //
  // The natural implementation is per-arrow: "for UP, find the
  // exit that best matches UP." Simulated against all 59 stops,
  // that leaves 16 of 148 roads UNWALKABLE BY KEYBOARD — because
  // when two roads leave a stop in similar directions the
  // stronger one wins every arrow it is even slightly good at,
  // and the weaker one wins nothing. From The Wood you could
  // never reach The Falls. There is no error, no warning, and
  // nothing on screen to suggest the road is not being offered.
  //
  // The fix is to stop asking each arrow what it wants and start
  // asking the STOP how to spend its four arrows: a tiny
  // assignment problem, maximise the NUMBER of exits that get an
  // arrow first, then the quality of fit. Count first is the
  // load-bearing half — maximising total fit alone still strands
  // seven roads, because the solver happily drops an exit if two
  // good pairings outscore three mediocre ones.
  //
  // Measured result: 4 of 148, which is the arithmetic floor —
  // four stops have FIVE walk exits and four arrows cannot
  // address five roads. Those four reach their fifth exit by tap
  // + Travel on the panel. Worst-fitting arrow anywhere on the
  // whole map is -0.04: perpendicular, never backwards.
  //
  // ── WALK LINKS ONLY ────────────────────────────────────────
  // Warps and secret exits are excluded, and that is the file's
  // own Two-Colour Law rather than a new rule: RED is a road you
  // WALK, YELLOW is a link you TAKE. An arrow key is a walk. A
  // yellow link relocates you across the world, which is not
  // something a directional tap should ever be able to do by
  // accident — those keep their own deliberate buttons, "Step
  // through the Seam" and "Travel", on the panel.
  const arrowMap = useMemo(() => {
    const from = NODE_BY_ID[at];
    if (!from) return [null, null, null, null];
    const exits = [];
    for (const [id, hop] of neighbors) {
      const via = hop?.path?.via || "clear";
      if (via === "warp" || via === "secret") continue;   // TAKE, not WALK
      const to = NODE_BY_ID[id]; if (!to) continue;

      const sx = to.mx - from.mx, sy = to.my - from.my;
      let lx = sx, ly = sy;
      if (hop.path) {
        const pts = routePoints(hop.path, hop.path.from === at);
        if (pts.length >= 2) {
          const ax = pts[1].x - pts[0].x, ay = pts[1].y - pts[0].y;
          // A first segment shorter than 6px is a rounding artefact,
          // not a direction — fall back to the straight line.
          if (Math.hypot(ax, ay) > 6) { lx = ax; ly = ay; }
        }
      }
      const lL = Math.hypot(lx, ly) || 1, sL = Math.hypot(sx, sy) || 1;
      exits.push({
        id,
        s: ARROW_VECS.map(([dx, dy]) =>
          0.75 * ((lx / lL) * dx + (ly / lL) * dy) +
          0.25 * ((sx / sL) * dx + (sy / sL) * dy)),
      });
    }
    return assignArrows(exits);
  }, [at, neighbors]);

  // Arrows AND WASD, Q/Enter to fight — the exact bindings the
  // Plaza already uses, so the hands do not have to relearn the
  // game between the town and the world. Bound to the window
  // rather than the wrap because the map has no focusable
  // surface and demanding a click before the keys work is the
  // kind of thing that reads as "the controls are broken."
  useEffect(() => {
    if (!progress) return;
    const AI = {
      ArrowUp: 0, ArrowDown: 1, ArrowLeft: 2, ArrowRight: 3,
      w: 0, s: 1, a: 2, d: 3,
    };
    const onKey = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) return;

      // `??` and not `||`, because ArrowUp is index 0 and 0 is falsy.
      // With `||` the up arrow would silently fall through to the
      // lowercase lookup, miss, and do nothing at all.
      const ai = AI[e.key] ?? AI[String(e.key).toLowerCase()];
      if (ai !== undefined) {
        // preventDefault unconditionally, even mid-walk: the whole
        // point is that arrows never scroll this screen, and a key
        // that scrolls only while the doll happens to be moving is
        // a bug that shows up once a session and never reproduces.
        e.preventDefault();
        const id = arrowMap[ai];
        if (!id) { setNudge("No road that way."); return; }
        setNudge("");
        step(id);
        return;
      }
      if (e.key === "q" || e.key === "Q" || e.key === "Enter") {
        e.preventDefault();
        const here = NODE_BY_ID[atRef.current];
        if (here) enter(here);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [progress, arrowMap, step, enter]);

  atRef.current  = at;
  const selNode  = selected ? NODE_BY_ID[selected] : null;
  // Standing on the map is a REST pose, not the true idle — the map
  // avatar should keep facing wherever it was last headed.
  const avatarSrc = walking ? getPlayerSprite(gender, facing.x, facing.y, walkFrame)
                            : getRestSprite(gender, facing.x, facing.y);

  if (!progress) {
    return (
      <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center",
        background: "#020107", color: GM.vellumDim, fontFamily: GM.font, letterSpacing: ".3em" }}>
        OPENING THE WORLD
      </div>
    );
  }

  return (
    <div ref={wrapRef} style={{ position: "absolute", inset: 0, overflow: "hidden",
      background: "#020107", touchAction: "none" }}
      onPointerDown={onPointerDown} onPointerMove={onPointerMove}
      onPointerUp={onPointerUp} onPointerCancel={onPointerUp}
      onContextMenu={(e) => e.preventDefault()}>

      {/* ══ WHAT IS BEHIND THE WORLD ══════════════════════════
          The player's own equipped backdrop, filling the whole
          surface instead of its usual chrome-rail band. Under
          the Frame Law the painting covers the frame, so most
          of the time this is never seen — which is the point.
          It is what the three unavoidable gaps look like: the
          load veil before the art decodes, a CDN miss on
          `overworld.webp`, and the single frame during a window
          resize before the clamp catches up. All three used to
          be flat #020107. None of them are now. */}
      <RoomBackdrop uid={uid} band={FULL_BAND} />

      <div ref={camRef} style={{ position: "absolute", left: 0, top: 0, zIndex: 1,
        transformOrigin: "0 0", willChange: "transform" }}>
        {/* The painting is a rectangle and a rectangle has corners,
            which is the tell that turns a world back into a
            picture the moment either one is visible. Rather than
            feather the art itself — that would fade real pins and
            real roads near the map's edge — the plane throws a
            deep soft shadow OUTWARD, so the last inch of the world
            dissolves into the backdrop instead of stopping at a
            line. Costs one box-shadow and no pixels of content. */}
        <div ref={planeRef} style={{ position: "relative", width: wmap.w, height: wmap.h,
          boxShadow: "0 0 70px 26px rgba(3,2,9,.92)" }}>

          {/* ══ THE PAINTING ══════════════════════════════════════
              The whole world, as one file. NO crossOrigin — the
              asset CDN sends no CORS headers and setting the
              attribute is exactly what turns art into dark slabs.
              draggable=false because the browser's native image
              drag would otherwise fight every pan gesture, and
              pointerEvents:none so the pins above it get the taps.
              imageRendering stays default: this is a painting, not
              pixel art, and nearest-neighbour would shred it. */}
          <img
            src={cdnUrl(wmap.file)}
            alt=""
            draggable={false}
            onLoad={() => setArtState("ok")}
            onError={() => setArtState("failed")}
            style={{ position: "absolute", left: 0, top: 0, width: wmap.w, height: wmap.h,
              pointerEvents: "none", userSelect: "none", display: "block" }}
          />

          {/* THE MISSING-ART PLANE. Only ever drawn when the painting
              failed to load. This is not a fallback look and nobody
              should ship on it — it is a MEASURING SURFACE. It shows
              exactly where the 1054x1492 world is and puts a 100px
              grid behind the pins, so one glance separates "the pins
              are wrong" from "the image is missing." Those two look
              identical against a black screen, and telling them
              apart is most of the debugging time. */}
          {artState === "failed" && (
            <div style={{ position: "absolute", left: 0, top: 0, width: wmap.w, height: wmap.h,
              pointerEvents: "none",
              background:
                "repeating-linear-gradient(0deg,rgba(255,255,255,.05) 0 1px,transparent 1px 100px)," +
                "repeating-linear-gradient(90deg,rgba(255,255,255,.05) 0 1px,transparent 1px 100px)," +
                "radial-gradient(120% 90% at 50% 40%,rgba(58,30,78,.55),rgba(6,3,14,.92))",
              border: "1px dashed rgba(255,120,160,.5)" }} />
          )}

          {/* ══ THE ROUTES ═══════════════════════════════════════
              Under the pins, over the painting. `overflow:visible`
              because a stroke's round cap can sit a pixel outside
              the viewBox at the map's edge. */}
          <svg width={wmap.w} height={wmap.h} viewBox={`0 0 ${wmap.w} ${wmap.h}`}
            style={{ position: "absolute", left: 0, top: 0, pointerEvents: "none", overflow: "visible" }}>
            {roads.filter((r) => (r.take ? SHOW_WARP_ROUTES : SHOW_WALK_ROUTES)).map((r) => (
              <g key={r.key} className="wmRd" data-e={r.key} data-vis="1">
                {/* casing first, so the core reads as light travelling
                    down a groove rather than as a line on top of art */}
                <path d={r.d} fill="none" stroke="rgba(4,2,10,.78)" strokeWidth="6"
                  strokeLinecap="round" strokeLinejoin="round" />
                <path d={r.d} fill="none" stroke={r.hue}
                  strokeWidth={r.take ? 2 : 2.6}
                  strokeLinecap="round" strokeLinejoin="round"
                  className={r.warp ? "wmWarp" : r.secret ? "wmSecret" : undefined}
                  opacity={r.take ? 0.95 : 0.9} />
              </g>
            ))}
          </svg>

          {/* ══ YOU ═══════════════════════════════════════════════ */}
          <div ref={shadowRef} className="wmShadow" style={{ width: 34, height: 13, zIndex: 28 }} />
          <div ref={avatarRef} className="wmPaper" style={{ zIndex: 29 }}>
            <div style={{ animation: walking ? "wmHop .52s ease-in-out infinite" : "wmBob 2.8s ease-in-out infinite" }}>
              <Sprite src={avatarSrc} size={50} fallbackColor={GM.inkGold} />
            </div>
            {companion?.companionId && (
              <div style={{ position: "absolute", left: -24, bottom: 0 }}>
                <CompanionSprite companionId={companion.companionId} size={32}
                  variant="standard" radiantTier={companion.radiantTier} />
              </div>
            )}
          </div>

          {/* ══ THE PINS ══════════════════════════════════════════ */}
          {worldNodes.map((n) => (
            <Pin key={n.id} n={n} st={nodeState(n.id, ctx, reachable)}
              here={n.id === at} sel={n.id === selected}
              onTap={() => tapNode(n)} />
          ))}
        </div>
      </div>

      <div className="wmGrade">
        <div className="wmBloom" /><div className="wmVig" /><div className="wmGrain" />
      </div>

      <div className="wmVeil" style={{ opacity: raised ? 0 : 1 }} />

      {artState === "failed" && <ArtMissing url={cdnUrl(wmap.file)} />}

      {/* Both camera jumps now pass a null zoom, which means KEEP
          THE PLAYER'S ZOOM. They used to snap to 0.85 and 0.95 —
          two more magic numbers that meant nothing in particular
          and quietly undid whatever framing the player had chosen
          before they tapped a region. A jump is a change of place,
          not a change of altitude. */}
      <Chrome playerName={playerName} regions={regions} kidsFound={kidsFound}
        shards={ctx.shards} secretsFound={secretsFoundCount(ctx)}
        tools={toolsHeld(progress)} veilLedger={progress?.veil}
        skyDoors={skyDoors} onSky={pressSky}
        onRegion={(rid) => {
          const first = worldNodes.find((n) => n.region === rid && reachable.has(n.id))
                     || worldNodes.find((n) => n.region === rid);
          if (first) centerOn(first.id, null, true);
        }}
        onHome={() => centerOn(at, null, true)}
        onZoomIn={() => zoomBy(1.35)}
        onZoomOut={() => zoomBy(1 / 1.35)}
        onWorld={fitWorld}
        onExit={onExit} />

      {selNode && (
        <NodePanel node={selNode} ctx={ctx} at={at} neighbors={neighbors}
          reachable={reachable} nudge={nudge}
          onEnter={enter} onStep={step} onJump={step} onWarp={warpThrough}
          /* ═══════════════════════════════════════════════════
             ⭐ THE WAY BACK — fix, 2026-08-20
             ═══════════════════════════════════════════════════
             THE EGG-DOOR SHIPPED AS A ONE-WAY DROP INTO A ROOM WITH
             NO EXITS, and the gentlest riddle in the world opened it.

             `AAC-01` (then `veil_camp`) is the START NODE and its secretReq is one
             Space being — the file itself calls it "answerable by a
             brand-new player with a starter deck." Answer it, press
             "Into the Eye", and you land on the pupil (then `rent_lash`), whose only
             outbound rows are `via: "clear"` and therefore need the
             pupil or a ring crack ALREADY CLEARED. A player who has
             never been in the Eye has cleared none of them, so
             `neighborsOf` returns an empty map: no Travel pill, no
             Seam pill, and a `difficulty: "Lord"` fight as the only
             thing to do. `step` saves position, so the trap persists
             into the save file.

             The fix is the law the beacons were already written under
             — *"A BEACON IS A PRIVATE DOOR, BOTH WAYS."* An egg-door
             is a door too. It remembers the doorstep it opened from
             and offers it back, and it costs one piece of state
             instead of a row in the graph, because the return is not
             a road that exists in the world — it is the same door,
             from the other side. */
          /* ⚠ THE PILL AND THE SKY DOOR SHARE ONE MEMORY. Both land on
             the pupil, and before this they each kept their OWN idea of
             where the player had come from — so entering by the pill
             and leaving by the Eye answered "You did not come this
             way." Two affordances for one journey is fine; two
             different ways home for it is not. */
          onEye={() => { setEyeFrom(at); rememberSky("eye", at); step(EYE_NODE, { warp: true }); }}
          eyeFrom={eyeFrom}
          onEyeBack={() => { const back = eyeFrom; setEyeFrom(null); if (back && NODE_BY_ID[back]) step(back, { warp: true }); }}
          onClose={() => { setSelected(null); setNudge(""); }} />
      )}

      {result && <ResultRibbon result={result} onClose={onClearResult} />}

      {/* ⬡ THE POP WINDOWS. Rendered LAST so they sit over the panel,
          the ribbon and the chrome — a payout that a node panel can
          cover is a payout the player scrolls past. */}
      <WorldNotice notices={notices} onClose={popNotice} />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ART MISSING — the CDN law, made visible
// ═══════════════════════════════════════════════════════════════
// The single most expensive bug class on this project is an asset
// URL that is wrong in a way nothing reports. The asset domain
// misses quietly; the APP domain is worse, because its catch-all
// SPA routing answers 200 with an HTML document for every path, so
// a broken image URL and a working one are indistinguishable by
// status code. Both end here, at `onerror`, with a black screen.
//
// So this panel exists to say the one thing that actually shortens
// the hunt: THE EXACT STRING WE ASKED FOR. Everything else is
// deduction the reader can do themselves once they can see it.
function ArtMissing({ url }) {
  return (
    <div data-chrome style={{
      position: "absolute", left: "50%", top: "50%", transform: "translate(-50%,-50%)",
      zIndex: 40, maxWidth: 520, padding: "18px 22px", borderRadius: 14,
      border: "1px solid #7a3050", background: "rgba(14,6,18,.96)",
      backdropFilter: "blur(10px)", boxShadow: "0 20px 70px rgba(0,0,0,.8)",
      pointerEvents: "auto", textAlign: "left",
    }}>
      <div style={{ fontFamily: GM.font, fontSize: 12, letterSpacing: ".22em",
        textTransform: "uppercase", color: "#ff8fae" }}>
        The world art did not load
      </div>
      <div style={{ marginTop: 10, fontSize: 12, lineHeight: 1.6, color: GM.vellumDim }}>
        Everything else on this screen is fine — the pins and routes are drawn
        at their real coordinates over a 100px grid. Only the image is missing.
      </div>
      <div style={{ marginTop: 11, padding: "8px 10px", borderRadius: 7,
        border: `1px solid ${GM.pageBorder}`, background: "#06040c",
        fontFamily: "ui-monospace,Menlo,Consolas,monospace", fontSize: 11,
        color: "#cbe3c6", wordBreak: "break-all", userSelect: "text" }}>
        {url}
      </div>
      <div style={{ marginTop: 11, fontSize: 12, lineHeight: 1.75, color: GM.vellumDim }}>
        Open that in a tab. Then, in order of likelihood:
        <br />1. <b style={{ color: GM.vellum }}>Case.</b> The CDN is case-sensitive.
        {" "}<code>Terror_ow_1.webp</code> and <code>terror_ow_1.webp</code> are different files.
        <br />2. <b style={{ color: GM.vellum }}>Not deployed.</b> In the assets folder
        {" "}locally is not the same as live on the Pages project.
        <br />3. <b style={{ color: GM.vellum }}>Wrong domain or a subfolder.</b>
        {" "}Art is never served from the app domain.
      </div>
      <div style={{ marginTop: 10, fontSize: 11, color: "#8d8397", fontStyle: "italic" }}>
        The filename lives in one place: <code>MAP.file</code>, top of worldData.js.
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// THE PIN — a mark sitting in a painted pad
// ═══════════════════════════════════════════════════════════════
// ── M10 · THE PINS BECOME CONTROLS ──────────────────────────────
// 54 pins were <div>s with an onPointerUp. That is invisible to a
// keyboard and silent to a screen reader — the whole world map was
// unreachable without a mouse, which is the one accessibility gap in
// Terror big enough to lock a player out of a mode rather than
// inconvenience them in it.
//
// ONE builder, used by all three of Pin's return branches, because the
// branch that gets forgotten is the one that gets added next. It
// supplies role, tabIndex, a spoken label and Enter/Space — and
// deliberately KEEPS onPointerUp rather than moving to onClick: the map
// is drag-panned, and pointer-up is what distinguishes a tap on a pin
// from the end of a pan across one. Changing that would have traded an
// accessibility fix for a feel regression.
//
// The label is composed, not authored, so a renamed node relabels
// itself: "The Roadhead, in the Marsh. Cleared. Press Enter to select."
const PIN_STATE_WORD = {
  locked:   "not yet reachable",
  open:     "open",
  cleared:  "cleared",
  next:     "reachable",
  current:  "you are here",
};

function pinProps(n, st, here, onTap) {
  const where = REGION_BY_ID[n.region]?.name;
  const state = here ? PIN_STATE_WORD.current : (PIN_STATE_WORD[st] || st || "");
  const label = [
    n.name || "An unnamed place",
    where ? `in ${where}` : null,
    state,
  ].filter(Boolean).join(". ") + ". Press Enter to select.";
  return {
    role: "button",
    tabIndex: 0,
    "aria-label": label,
    "aria-current": here ? "location" : undefined,
    "aria-disabled": st === "locked" ? true : undefined,
    onPointerUp: (e) => { e.stopPropagation(); onTap(); },
    onKeyDown: (e) => {
      if (e.key === "Enter" || e.key === " " || e.key === "Spacebar") {
        e.preventDefault(); e.stopPropagation(); onTap();
      }
    },
  };
}

function Pin({ n, st, here, sel, onTap }) {
  const a11y = pinProps(n, st, here, onTap);
  const face = faceFor(n.kind);
  const hue  = n.kind === "child" ? "#ffd47a" : regionHue(n.region);

  // ── THE BARE MAP. The painted pad IS the mark, so the disc is
  //    not drawn — but the node is still a node. This branch keeps
  //    the full hit area and the selection ring and paints nothing
  //    else, which is the difference between "the icons are off"
  //    and "the map stopped working." A selected node still shows
  //    a thin ring, because a click with no feedback reads as a
  //    dead click, and checking the road is the whole job here.
  if (!SHOW_PIN_MARKS) {
    return (
      <div className="wmPin" data-id={n.id} data-st={st} data-sel={sel ? "1" : "0"} {...a11y}
        style={{ left: n.mx, top: n.my, width: 26, height: 26, zIndex: here ? 25 : 10 }}>
        {sel && (
          <div style={{ position: "absolute", inset: 0, borderRadius: "50%",
            border: "1.5px solid rgba(255,255,255,.85)", boxShadow: "0 0 12px rgba(255,255,255,.35)" }} />
        )}
      </div>
    );
  }

  // UNREACHED — a mark on the ground, not a monument on it.
  if (st === "locked") {
    return (
      <div className="wmPin" data-id={n.id} data-st="locked" {...a11y}
        style={{ left: n.mx, top: n.my, zIndex: 4 }}>
        <div style={{
          width: 11, height: 11, transform: "rotate(45deg)",
          border: "1.5px solid rgba(160,150,175,.85)", background: "rgba(8,6,14,.8)",
        }} />
      </div>
    );
  }

  const cleared = st === "cleared";
  return (
    <div className="wmPin" data-id={n.id} data-st={st} data-sel={sel ? "1" : "0"} {...a11y}
      style={{ left: n.mx, top: n.my, width: face.size, height: face.size,
        zIndex: here ? 25 : 10 }}>
      {here && (
        <div style={{ position: "absolute", inset: -9, borderRadius: "50%",
          border: "1.5px solid #fff", opacity: 0.5, animation: "wmRipple 3s ease-out infinite" }} />
      )}
      <div style={{
        width: face.size, height: face.size,
        borderRadius: n.kind === "fort" || n.kind === "throne" ? 6 : "50%",
        display: "grid", placeItems: "center",
        background: cleared ? "rgba(16,28,16,.9)" : "rgba(10,6,20,.9)",
        border: `2px solid ${here ? "#fff" : cleared ? "#8fd694" : hue}`,
        color: cleared ? "#cfe6c6" : hue,
        fontSize: Math.round(face.size * 0.46), lineHeight: 1,
        boxShadow: here ? "0 0 20px rgba(255,231,176,.65)" : `0 0 12px ${hue}55`,
      }}>{cleared ? GLYPH.check : face.glyph}</div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// CHROME — floats over the world, never steals height from it
// ═══════════════════════════════════════════════════════════════
function Pill({ children, tone = "dim", onClick, disabled, wide }) {
  const tones = {
    dim:    { bg: "rgba(17,12,8,.82)", bd: GM.pageBorder, fg: GM.vellumDim },
    action: { bg: `linear-gradient(180deg,${GM.inkGoldBright},${GM.inkGold})`, bd: GM.inkGoldBright, fg: "#120c06" },
    pink:   { bg: `linear-gradient(180deg,#ff5c8a,${PINK})`, bd: "#ff5c8a", fg: "#1a0008" },
  };
  const t = tones[tone] || tones.dim;
  return (
    <button data-chrome onClick={onClick} disabled={disabled}
      style={{
        fontFamily: GM.font, fontSize: 10, letterSpacing: ".14em", textTransform: "uppercase",
        padding: wide ? "9px 18px" : "7px 11px", border: `1px solid ${t.bd}`, borderRadius: 999,
        background: t.bg, color: t.fg, cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.4 : 1, backdropFilter: "blur(6px)", whiteSpace: "nowrap",
      }}>{children}</button>
  );
}

function Chrome({ playerName, regions, kidsFound, shards, secretsFound, tools, veilLedger, skyDoors, onRegion, onHome, onExit, onZoomIn, onZoomOut, onWorld, onSky }) {
  // Controls live TOP-LEFT, under the title. They were top-right, where
  // they collided with the host app's own MENU button — a real bug you
  // only ever see in a screenshot of the shipped screen, never in the
  // component. The right edge belongs to the app shell; this screen
  // does not get to put anything there.
  return (
    <>
      <div data-chrome style={{
        position: "absolute", left: 0, top: 0, zIndex: 20, padding: "11px 14px",
        pointerEvents: "none", maxWidth: 340,
        background: "linear-gradient(160deg,rgba(3,2,10,.92),rgba(3,2,10,0) 78%)",
      }}>
        <div style={{ pointerEvents: "auto" }}>
          <div style={{ fontFamily: GM.font, fontWeight: 900, fontSize: 14, letterSpacing: ".17em",
            color: GM.inkGoldBright, textTransform: "uppercase", textShadow: "0 0 18px rgba(255,34,102,.4)" }}>
            The Unstable World
          </div>
          <div style={{ fontFamily: GM.font, fontSize: 9, letterSpacing: ".3em", color: "#ff7fa8" }}>
            {playerName || "Aenarchist"} {GLYPH.dash} {kidsFound.length}/{CHILDREN.length} found
          </div>

          {/* THE SHARDS YOU CARRY. T19 said it about Anima and it is
              just as true here: a resource the screen does not show
              is a resource the player cannot plan around, and shards
              are the only thing on this map that gates a whole
              arena. Drawn out of the TOTAL on purpose -- "6 / 24"
              answers "is this door even reachable" in one glance,
              which a bare 6 never does. */}
          <div style={{ marginTop: 5, fontFamily: GM.font, fontSize: 9, letterSpacing: ".26em",
            color: shards > 0 ? "#ffd47a" : GM.vellumDim, textTransform: "uppercase" }}>
            {GLYPH.diamond} {shards} / {SHARD_TOTAL} Sol Shards
          </div>

          {/* THE EGGS. Only appears once the player has found one,
              because a 0/15 counter on a brand-new save is telling
              somebody about a system they have not met yet — and
              the first secret is supposed to be a surprise, not an
              item on a checklist they were handed at the door. */}
          {secretsFound > 0 && (
            <div style={{ marginTop: 3, fontFamily: GM.font, fontSize: 9, letterSpacing: ".26em",
              color: secretsFound >= SECRET_NODES.length ? "#ff8fb4" : GM.vellumDim,
              textTransform: "uppercase" }}>
              {GLYPH.star4} {secretsFound} / {SECRET_NODES.length} secrets
            </div>
          )}

          {/* ⬡ WHAT IS ON YOU, AND WHAT IT IS EATING.
              Same argument as the shard counter directly above: a
              resource the screen does not show is a resource the
              player cannot plan around — and the Veil is worse than a
              resource, because its whole rule is invisible. A player
              who does not know they are cloaked will warp, break it,
              and never learn what the window was about.
              Absent until she lends you something, so a fresh save is
              not told about a system it has not met. */}
          {(tools || []).map((t) => {
            const owed = ledgerLine(veilLedger);
            return (
              <div key={t.id} style={{ marginTop: 5 }}>
                <div style={{ fontFamily: GM.font, fontSize: 9, letterSpacing: ".26em",
                  color: t.accent || GM.inkGoldBright, textTransform: "uppercase" }}>
                  {GLYPH.diamondOpen} {t.name}
                </div>
                <div style={{ fontFamily: GM.font, fontSize: 8, letterSpacing: ".18em",
                  color: GM.vellumFaint, textTransform: "uppercase", opacity: .8 }}>
                  {owed ? `${veilLedger?.steps || 0} crept ${GLYPH.dash} ${owed}` : "walk, do not cut"}
                </div>
              </div>
            );
          })}

          {/* ═══════════════════════════════════════════════════
              ☾ ◉ THE SKY DOORS
              ═══════════════════════════════════════════════════
              caP'n, 2026-08-21, on the Moon: *"the moon burns fiercely
              with aura and you can travel to the Crimson Dais."* And
              on the Eye: *"you can now click on it proper and travel
              to the middle of the eyeball."*

              TWO buttons now, from one table, because they are one
              mechanism — see SKY_DOORS in worldData. This block used
              to iterate `emperiums` and could therefore only ever draw
              a door that had a FARE; the Eye has no fare, its
              condition is six keepers or four eggs, and it would have
              had no home here at all.

              THE AURA IS THE WHOLE POINT and it is deliberately not
              subtle — two stacked shadows plus a keyframed pulse, and
              it only ever runs while `lit` is true. A door that glowed
              at 3 of 15 would be a door that taught the player its
              glow means nothing. */}
          {(skyDoors || []).map((d) => {
            const live = !!d.live;
            const lit  = !!d.lit;
            // `finished` is the boolean, `done` is the sentence — they
            // used to share a name and the boolean ate the sentence.
            const sub  = d.finished ? (d.done || "")
              : d.paid ? d.open
              : lit ? d.lit
              : d.emperium ? `${d.cost} Sol Shards — ${d.shut}`
              : d.shut;
            return (
            <button key={d.id} data-chrome
              disabled={!live}
              onClick={() => onSky && onSky(d)}
              style={{
                marginTop: 9, display: "flex", alignItems: "center", gap: 11,
                width: "100%", textAlign: "left",
                fontFamily: GM.font, padding: "9px 11px", borderRadius: 10,
                cursor: live ? "pointer" : "default",
                border: `1px solid ${live ? d.accent : GM.pageBorder}`,
                background: live ? "rgba(60,4,22,.62)" : "rgba(9,7,12,.7)",
                color: live ? "#ffe0ea" : "#7e7488",
                backdropFilter: "blur(6px)",
                animation: lit ? "wmMoonPulse 2.6s ease-in-out infinite" : "none",
              }}>
              {/* The disc. Dead grey until the door is open, and then a
                  lit rim with its own halo — the glyph never changes,
                  only whether anything is behind it. */}
              <span style={{
                flex: "0 0 auto", width: 30, height: 30, borderRadius: "50%",
                display: "grid", placeItems: "center", fontSize: 17, lineHeight: 1,
                border: `1px solid ${live ? d.accent : "#3a3140"}`,
                color: live ? "#fff2f6" : "#5d5566",
                // ⭐ THE BURN. A door that is merely LIVE glows in its
                // own accent; a door that is LIT — the Moon at fifteen
                // Sol Shards — burns in its `auraCore`/`auraRim` pair
                // instead, bone-white bleeding to blood-red. Two
                // different states that used to look like one state
                // slightly brighter, which is the whole reason caP'n
                // asked for the Moon to have an aura of its own.
                background: live
                  ? (lit && d.auraCore
                      ? `radial-gradient(circle at 34% 30%, ${d.auraCore} 0%, ${d.accent} 48%, ${d.auraRim || d.accent} 78%, rgba(0,0,0,.92) 100%)`
                      : `radial-gradient(circle at 34% 30%, #fff 0%, ${d.accent} 62%, rgba(0,0,0,.9) 100%)`)
                  : "radial-gradient(circle at 34% 30%, #2a2530 0%, #14111a 70%)",
                boxShadow: lit
                  ? `0 0 12px ${d.auraCore || "#fff"}cc, 0 0 26px ${d.accent}, 0 0 54px ${(d.auraRim || d.accent)}88`
                  : "none",
              }}>{d.glyph}</span>
              <span style={{ minWidth: 0 }}>
                <span style={{ display: "block", fontSize: 9.5, letterSpacing: ".2em",
                  textTransform: "uppercase" }}>
                  {d.label}
                </span>
                <span style={{ display: "block", marginTop: 3, fontSize: 8.5,
                  letterSpacing: ".08em", opacity: 0.86 }}>
                  {sub}
                </span>
              </span>
            </button>
            );
          })}

          {/* ⚠ THE SURVEYOR BADGE. A test mode you cannot see is a
              test mode that ships. This one changes what the world
              LETS you do without changing what the world LOOKS
              like, which is the most shippable kind of mistake
              there is — so it says so, on screen, in the one place
              the eye already goes. Disappears with the dial. */}
          {SURVEYOR && (
            <div style={{ marginTop: 6, display: "inline-block",
              fontFamily: GM.font, fontSize: 8, letterSpacing: ".28em",
              textTransform: "uppercase", padding: "3px 8px", borderRadius: 4,
              border: "1px solid #ff2266", color: "#ffb8cc",
              background: "rgba(60,4,22,.55)" }}>
              Surveyor {GLYPH.dash} all gates open
            </div>
          )}
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 10 }}>
            {/* "WORLD" IS DELETED (caP'n, July 29). It was the wide
                shot — the whole painting small in the middle of a
                black frame — and it turned out to be the exact
                thing that broke the spell. A screen cannot both
                promise you are standing in a place and offer a
                button that shows you the place is a picture. The
                wide shot loses; the place wins. "You" stays,
                because recentring on yourself is navigation, not a
                change of camera. */}
            <Pill onClick={onHome}>{GLYPH.diamond} You</Pill>
            {onExit && <Pill onClick={onExit}>Leave</Pill>}
          </div>

          {/* THE DIAL, ON SCREEN (August 17, 2026). "World" is back
              — see THE WORLD SHOT, RESTORED at the top of this file
              for why the July 29 deletion above was re-decided. It
              is a working tool now: you cannot coordinate ten
              beacons across a 841x1264 painting through a keyhole.
              Left edge, same rail as everything else, because the
              right edge belongs to the app shell's MENU. */}
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 6 }}>
            <Pill onClick={onZoomOut}>{"\u2212"}</Pill>
            <Pill onClick={onZoomIn}>+</Pill>
            <Pill onClick={onWorld}>World</Pill>
          </div>
        </div>
      </div>

      <div data-chrome style={{
        position: "absolute", left: 14, bottom: 14, zIndex: 19,
        display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-start", pointerEvents: "auto",
      }}>
        {regions.map((r) => (
          <button key={r.id} data-chrome onClick={() => onRegion(r.id)} style={{
            fontFamily: GM.font, fontSize: 9, letterSpacing: ".15em", textTransform: "uppercase",
            padding: "7px 11px", border: `1px solid ${GM.pageBorder}`, borderRadius: 999,
            background: "rgba(9,7,12,.84)", color: GM.vellumDim, cursor: "pointer",
            display: "flex", alignItems: "center", gap: 7, whiteSpace: "nowrap", backdropFilter: "blur(6px)",
          }}>
            <i style={{ width: 7, height: 7, borderRadius: "50%", display: "block",
              background: r.hue, boxShadow: `0 0 9px ${r.hue}` }} />
            {r.name}
          </button>
        ))}
      </div>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════
// THE NODE PANEL — unchanged rules, new frame
// ═══════════════════════════════════════════════════════════════
function NodePanel({ node, ctx, at, neighbors, reachable, nudge, onEnter, onStep, onJump, onWarp, onEye, eyeFrom, onEyeBack, onClose }) {
  const st       = nodeState(node.id, ctx, reachable);
  const here     = node.id === at;
  const isNext   = neighbors.has(node.id);
  const boss     = resolveBoss(node.boss);
  const face     = faceFor(node.kind);
  const secret   = secretOffered(node, ctx);
  const locked   = st === "locked";
  const warpOut  = PATHS.some((p) => p.from === node.id && p.via === "warp" && pathState(p, ctx).open);
  const child    = CHILDREN.find((c) => c.node === node.id);
  const kidHere  = child && !ctx.found.has(child.id);
  // ⭐ THE EGG IS A DOOR. See eyeStepFrom in worldData — every node
  // whose puzzle was answered can step straight into the pupil,
  // instead of walking the ring. Derived, so a new secretReq
  // anywhere hands that node this pill and no list goes stale.
  const eyeOut   = here && eyeStepFrom(node, ctx);

  return (
    <div data-chrome style={{
      position: "absolute", left: "50%", bottom: 14, transform: "translateX(-50%)", zIndex: 22,
      width: "min(620px, calc(100% - 240px))", borderRadius: 14, padding: "14px 18px 16px",
      border: `1px solid ${GM.pageBorder}`, backdropFilter: "blur(12px)",
      background: "linear-gradient(180deg,rgba(26,18,11,.96),rgba(7,5,10,.98))",
      boxShadow: "0 -14px 60px rgba(0,0,0,.7)", pointerEvents: "auto",
    }}>
      <div onClick={onClose} style={{ position: "absolute", right: 12, top: 8, cursor: "pointer",
        color: GM.vellumDim, fontSize: 15 }}>{"\u2715"}</div>

      <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
        <h3 style={{ margin: 0, fontFamily: GM.font, fontSize: 19, letterSpacing: ".05em",
          color: GM.inkGoldBright }}>
          {locked ? `${GLYPH.dash} unreached ${GLYPH.dash}` : node.name}
        </h3>
        <Tag>{face.label}</Tag>
        {/* THE FOUR MARKS. The pad already knew what it was; until
            today only the pin glyph said so and the panel did not.
            A Cursed Land reading "Cursed Land" is the cheapest
            possible way to tell a player that plain IS the design
            here and they are not missing a puzzle. */}
        {!locked && roleLabel(node) && (
          <Tag tone={nodeRole(node)}>{MARK_ROLES[node.mark].glyph} {roleLabel(node)}</Tag>
        )}
        <Tag tone={st}>{st === "cleared" ? "Cleared" : st === "open" ? "Open" : "Sealed"}</Tag>
        {here && <Tag tone="here">You are here</Tag>}
      </div>

      <p style={{ margin: "9px 0 0", fontSize: 15, lineHeight: 1.5, color: GM.vellum,
        fontStyle: "italic", opacity: 0.94 }}>
        {locked
          ? "\u201cYou have not been here. Nothing has told you what is waiting.\u201d"
          : `\u201c${node.flavor || ""}\u201d`}
      </p>

      {!locked && (
        <div style={{ marginTop: 9, fontSize: 12, color: GM.vellumDim, lineHeight: 1.7 }}>
          {boss && <>Held by <em style={{ color: "#ff7fa8", fontStyle: "normal" }}>{boss.name}</em>{"  \u00b7  "}</>}
          {/* ⭐ THE SPECIES LAW made visible. Every being in the
              Minerium is one of the canon twelve, and until now the
              only way to find out which was to read the code. A player
              deciding what to bring to a puzzle is exactly the player
              who wants to know what is standing there. */}
          {(node.foe?.species || node.species) && (
            <>A <em style={{ color: "#ffd47a", fontStyle: "normal" }}>{node.foe?.species || node.species}</em>{"  \u00b7  "}</>
          )}
          Difficulty <em style={{ color: "#ff7fa8", fontStyle: "normal" }}>{node.difficulty}</em>
          {node.reward?.crests ? <>{"  \u00b7  "}<em style={{ color: "#ff7fa8", fontStyle: "normal" }}>{node.reward.crests}</em> coins</> : null}
          {kidHere && <>{"  \u00b7  "}<em style={{ color: "#ffd47a", fontStyle: "normal" }}>{UNKNOWN_CHILD}</em></>}
          {secret && <>{"  \u00b7  "}<em style={{ color: SILVER, fontStyle: "normal" }}>A secret opens here</em></>}
          {shardsFor(node) > 0 && (
            <>{"  \u00b7  "}<em style={{ color: "#ffd47a", fontStyle: "normal" }}>
              {shardsFor(node)} Sol {shardsFor(node) === 1 ? "Shard" : "Shards"}
            </em>{ctx.shardNodes.has(node.id) ? " (taken)" : " held by its keeper"}</>
          )}
        </div>
      )}

      {/* ── THE RIDDLE ──────────────────────────────────────────
          caP'n: "there is a hint when you click on this node."
          Literally that: the hint is on the panel, the puzzle is
          the node's own secretReq, and the line above already says
          whether you are currently carrying the answer. A Chamber
          with no authored hint says nothing rather than saying
          something empty — it is still beatable the ordinary way. */}
      {!locked && chamberHint(node) && (
        <div style={{
          marginTop: 10, padding: "9px 12px", borderRadius: 9,
          border: `1px solid ${secret ? "#cfe6c6" : GM.pageBorder}`,
          background: "rgba(0,0,0,.35)",
          fontSize: 12.5, lineHeight: 1.65,
          color: secret ? "#cfe6c6" : GM.vellumDim, fontStyle: "italic",
        }}>
          <span style={{ letterSpacing: ".18em", fontStyle: "normal", fontSize: 9,
            color: GM.vellumDim, display: "block", marginBottom: 4 }}>
            {"\u25c8"} THE RIDDLE
          </span>
          {chamberHint(node)}
          {secret && <div style={{ marginTop: 5, fontStyle: "normal", fontSize: 11 }}>
            You are carrying the answer. Win here and the door is yours.
          </div>}
        </div>
      )}

      {nudge && <div style={{ marginTop: 8, fontSize: 12, color: "#ff9db8", fontStyle: "italic" }}>{nudge}</div>}

      <div style={{ marginTop: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
        {here && warpOut && <Pill tone="pink" wide onClick={() => onWarp(node)}>{GLYPH.star4} Step through the Seam</Pill>}
        {eyeOut && <Pill tone="pink" wide onClick={() => onEye && onEye()}>{GLYPH.star4} Into the Eye</Pill>}
        {/* THE WAY BACK. Only in the pupil, only when a door brought
            you here, and it closes behind you once used. */}
        {here && node.id === EYE_NODE && eyeFrom && NODE_BY_ID[eyeFrom] && (
          <Pill tone="pink" wide onClick={() => onEyeBack && onEyeBack()}>
            {GLYPH.star4} Back to {NODE_BY_ID[eyeFrom].name}
          </Pill>
        )}
        {here && <Pill tone="action" wide onClick={() => onEnter(node)}>{GLYPH.shield} Enter</Pill>}
        {!here && isNext && <Pill tone="action" wide onClick={() => onStep(node.id)}>{GLYPH.diamond} Travel</Pill>}
        {/* ⚠ SURVEYOR. `onStep` already handles this for free: a
            hop with no path between the two stops falls through to
            the instant cut that warps use, avatar placed and
            position saved, no animation to invent. A jump is a warp
            with nobody's permission. */}
        {!here && !isNext && (SURVEYOR
          ? <Pill tone="pink" wide onClick={() => onJump(node.id)}>{GLYPH.star4} Jump here</Pill>
          : <Pill disabled wide>{locked ? "No road there" : "One step at a time"}</Pill>)}
      </div>
    </div>
  );
}

function Tag({ children, tone }) {
  const c = tone === "cleared" ? "#cfe6c6" : tone === "locked" ? "#8d8397"
          : tone === "here"    ? "#ffe7b0"
          : tone === "chamber" ? "#cfe6c6"   // green, like its beacon
          : tone === "shard"   ? "#ffd47a"
          : tone === "secret"  ? "#ff8fb4"
          : tone === "cursed"  ? "#b9a6d4"
          : GM.vellumDim;
  return (
    <span style={{ fontFamily: GM.font, fontSize: 9, letterSpacing: ".17em", textTransform: "uppercase",
      padding: "3px 8px", borderRadius: 999, border: `1px solid ${GM.pageBorder}`, color: c }}>{children}</span>
  );
}

function ResultRibbon({ result, onClose }) {
  if (!result) return null;
  const win = result.win ?? result.firstClear;
  return (
    <div data-chrome onClick={onClose} style={{
      position: "absolute", left: "50%", top: 74, transform: "translateX(-50%)", zIndex: 24,
      padding: "9px 20px", borderRadius: 999, cursor: "pointer", pointerEvents: "auto",
      border: `1px solid ${win ? GM.inkGoldBright : GM.pageBorder}`,
      background: "rgba(10,6,16,.9)", backdropFilter: "blur(8px)",
      fontFamily: GM.font, fontSize: 11, letterSpacing: ".2em", textTransform: "uppercase",
      color: win ? "#ffe7b0" : GM.vellumDim,
    }}>
      {/* The keeper's shards outrank the road, because a road
          opening is what EVERY win says and shards are what makes
          this one different. A ribbon that says the same sentence
          for both is a ribbon nobody reads twice. */}
      {/* Ranked by what is rarest, not by what happened last. The
          prize outranks the road and the shards because answering a
          riddle is the thing a player will want confirmed. */}
      {/* ⚠ THE MINERIUM OUTRANKS THE EGG, AND IT HAS TO.
          Every Mythic wedge fires on `firstSecret`, and recordNodeClear
          pays `eggCrests` on EVERY firstSecret — so while `prize` and
          `eggCrests` sat above `storySet` in this chain, the two
          Minerium lines below were unreachable code. A player could be
          handed sixteen Mythic cards and be told "10 Divine Crests".
          Cards are rarer than Crests; the rarest true thing wins the
          ribbon, which is the rule the rest of this chain already
          states. Found by an adversarial pass over this same commit. */}
      {result.storySet ? (
            // ⭐ THE BLUEPRINT, SAID OUT LOUD. A node pays the same
            // bundle either way, so there are only three things this
            // can be, and the player should be able to tell them apart
            // without opening a menu:
            //   · BOTH — egged on the first attempt, so the plain copy
            //     and the foiled copy landed together. The best
            //     sentence in the game and it deserves its own line.
            //   · MYTHIC — the egg on a node already cleared.
            //   · FABLE — an ordinary clear.
            // The room's own name is in every one of them, because the
            // whole point of a per-node loot map is that the player can
            // learn it.
            result.storySet.cosmetics
              ? `${GLYPH.star4} The Eye opens ${GLYPH.dash} his room, his coin, his light`
              : result.storySet.both
                ? `${GLYPH.star4} ${result.storySet.label} ${GLYPH.dash} claimed, and seared`
                : result.storySet.tier === "mythic"
                  ? `${GLYPH.star4} The Griddle finds you ${GLYPH.dash} ${result.storySet.label}, in Mythic`
                  : `${GLYPH.star4} The Minerium pays ${GLYPH.dash} ${result.storySet.label}`
          )
        : result.prize ? `${GLYPH.star4} The riddle answered ${GLYPH.dash} ${result.prize} coins${result.eggCrests ? ` and ${result.eggCrests} Divine ${result.eggCrests === 1 ? "Crest" : "Crests"}` : ""}`
        : result.eggCrests ? `${GLYPH.star4} The riddle answered ${GLYPH.dash} ${result.eggCrests} Divine ${result.eggCrests === 1 ? "Crest" : "Crests"}`
        : result.child ? `${GLYPH.star4} You found them`
        : (win && result.shards) ? `${GLYPH.diamond} The keeper yields ${result.shards} Sol ${result.shards === 1 ? "Shard" : "Shards"}`
        : win ? `${GLYPH.check} The road opens`
        : `${GLYPH.dash} Not this time`}
    </div>
  );
}
